// frontend/src/components/QuoteCreateModal.jsx
// Project:      Vision ANPR by Castle Traffic Ltd - Automatic Number Plate Recognition
// Author:       Leon du Plooy - Castle Traffic Ltd - Copyright 2026
// Contact:      dev@castle-traffic.co.uk
// Created:      2026-01-27
// Updated:      2026-10-30 09:02 GMT
// 2026-10-30 09:02 GMT: Show Units defaults unchecked for new quotes; saved preference remains authoritative.
// 2026-10-27 14:57 GMT: Multi-option labels explicitly use text-gray-600.
// 2026-10-27: Numbered-list injection without empty spacer paragraphs; selected text remains editable.
// 2026-10-27: Tenant-scoped reusable Specific Notes multi-select HTML injector.
//
// Update (2026-10-27):
//   - Specific Notes now uses the shared Tiptap HTML editor and retains HTML in quotes.specific_notes.
//   - Font controls and PDF HTML rendering require updates to their respective owner files.
//
// Update (2026-09-01):
//   - Pass Quote commercial provenance and actual line items to NextStepCard for Quote→Job creation.
//
// Description:
//   Create/Edit Quote modal.
//   - Draft lifecycle: Create → Save → Preview
//   - Preview is blocked while Dirty (save first) unless forced (Save & Refresh)
//
// IMPORTANT:
//   Uses shared Axios instance for tenant-safe API calls.
//
// Fix (2026-09-18):
//   - Quote image captions can now be saved independently of selecting a new image.
//   - Clearing an existing caption persists correctly by re-posting the stored image
//     through the existing image-upload contract with an explicit empty caption.
//   - Image-caption edits participate in Save / Save & Refresh / preview-stale gating.
//
// Caption save correction (2026-09-18):
//   - Caption edits now set the quote dirty flag immediately so the primary
//     Save and Save & Refresh controls activate on typing or clearing text.
//   - Existing caption-only persistence through the quote-image endpoint is preserved.
//
// Responsive mobile workspace (2026-09-16):
//   - Desktop two-column quote editor remains unchanged at lg+.
//   - Mobile uses dedicated Details / Items / Preview / More workspaces instead of one deeply nested page.
//   - Mobile line-item tools use Entry / Pricebook / Current sub-views.
//   - Standard and multi-option line items render as editable cards on mobile; desktop tables remain intact.
//   - Quote image slots use a mobile slot switcher so only one uploader consumes screen space at a time.
//   - PDF preview receives a dedicated mobile workspace, tall embedded viewer, and Open PDF fallback.
//   - Existing create/save/save-and-refresh/dirty/preview governance and API behaviour are preserved.
import React, { useEffect, useMemo, useRef, useState, useCallback } from "react";
import { createPortal } from "react-dom";
import TiptapEmailEditor from "./TiptapEmailEditor.jsx";
import NextStepCard from "./NextStepCard.jsx";
import axiosInstance from "../api/axiosInstance";
import { useTheme } from "../context/ThemeContext";

const htmlToPlainText = (html) => {
  const raw = String(html ?? "");
  const withoutTags = raw
    .replace(/<br\s*\/?>/gi, "\n")
    .replace(/<\/p\s*>/gi, "\n")
    .replace(/<[^>]+>/g, " ");
  return withoutTags
    .replace(/&nbsp;/gi, " ")
    .replace(/&amp;/gi, "&")
    .replace(/&lt;/gi, "<")
    .replace(/&gt;/gi, ">")
    .replace(/&quot;/gi, "\"")
    .replace(/&#39;/gi, "'")
    .replace(/&#039;/gi, "'")
    .replace(/\s+\n/g, "\n")
    .replace(/\n\s+/g, "\n")
    .replace(/[ \t]{2,}/g, " ")
    .trim();
};

//
// Updates (2026-02-06):
//   - Fix: Save & Refresh reliably refreshes preview (avoids stale dirty closure).
//   - Fix: Preview requests are cache-busted (nonce) and iframe is re-keyed.
//   - Fix: After PATCH, local form fields are re-synced from response payload (source of truth).
//
// Hotfix (2026-02-06):
//   - Fix: Terms dropdowns now display friendly labels but ALWAYS persist/send the *actual* terms_documents.id.
//   - Fix: Supports both numeric and UUID ids without silently coercing to null.
//   - Fix: "No Terms" is always null in payload (never "No Terms" / "" / label).
//
// Update (2026-02-09):
//   - Remove: "Sync table to body" (auto-injection) functionality entirely.
//     Line items are now only injected into the body via the manual "Insert/Update Table" action.
//
// Update (2026-02-10):
//   - FIX: Line items now persist via Quote Line Items API (same behaviour as QuoteLineItemsWidget):
//     - Loads via GET /api/quotes/{id}/line-items
//     - Adds via POST /api/quotes/{id}/line-items
//     - Updates via PATCH /api/quotes/{id}/line-items/{line_item_id}
//     - Deletes via DELETE /api/quotes/{id}/line-items/{line_item_id}
//     - Guards mutations using /api/quotes/{id}/status/meta -> meta.line_items_editable
//   - IMPORTANT: No trailing slashes in DELETE/PATCH routes (avoids 307 redirect failure).
//
// Update (2026-02-10) Styling/UX tidy pass:
//   - FIX: "Reload terms" now uses standard general button styling and clear label.
//   - FIX: Per-line delete button now uses standard general palette, readable "×", and consistent sizing.
//   - REMOVE: Insert/Update Table button + related body-injection logic (no longer required).
//   - REMOVE: Line-items header Reload button (not required; list auto-loads and is refreshed after mutations).
//   - Keep: Remove Table (body cleanup) since it is still useful if a legacy table exists in body_html.
//
// Update (2026-02-11):
//   - ADD: Title field (was missing in modal; existed in Quote details widget).
//     - Title is sent on CREATE and PATCH.
//     - Title is loaded on EDIT and re-synced from PATCH response payload.
//
// Update (2026-02-12):
//   - HARDEN (2): Prevent accidental line-item DELETE/PATCH on collection routes by guarding missing ids.
//   - WIRE (3): Lock ALL quote editing when status != draft/submitted (mirrors DB rules).
//     Status is derived defensively from quote payload + /status/meta when available.
//
// Update (2026-02-12):
//   - HARDEN (4): Do not wipe contact email while contacts are still loading (prevents blank "To" in email modal).
//   - WIRE (5): Pass contact id/email into NextStepCard for email pre-population.
//
// Update (2026-02-16):
//   - FIX: Wire quote-catalog endpoints (backend exposes /api/quote-catalog/*; NOT /api/pricebooks/*)
//   - UX: Keep pricebook dropdown; REMOVE free-text "Catalog / Pricebook search" field.
//
// Update (2026-02-17):
//   - FIX: Customer-scoped pricebooks were not appearing.
//     Root cause: modal was calling /pricebooks/eligible without passing customer context; backend filters those out.
//     Fix: fetch full active pricebooks list from /pricebooks (same as your curl) and filter client-side by customer_id/resource_id/tenant-wide.
//     Also: clear selection when customer changes and previously selected PB is no longer eligible.
//
// FIXES (2026-02-19):
//   - FIX 1: optLiSort / setOptLiSort → optLiSortOrder / setOptLiSortOrder (undefined variable).
//   - FIX 2: pricebookItems → pbItems (undefined variable; correct state name is pbItems).
//   - FIX 3: Multi-option JSX panel was nested inside the !multiOptionEnabled truthy branch instead
//            of being its own : (else) branch of the ternary. Restructured so the ternary correctly
//            renders either the standard line-items editor OR the multi-option panel.


// ---- helpers ---------------------------------------------------------------
const DEBUG = false;

// If your DB uses different keys, change these two only.
const QUOTE_TERMS_KEY = "quotes_terms";
const SERVICE_TERMS_KEY = "service_terms";

// quote-catalog base (backend exposes these; NOT /api/pricebooks/*)
const QUOTE_CATALOG_BASE = "/api/quote-catalog";
const PRICEBOOKS_BASE = `${QUOTE_CATALOG_BASE}/pricebooks`;

const norm = (v) => String(v ?? "").trim();
const toIntOrNull = (v) => {
  const s = norm(v);
  if (!s) return null;
  const n = Number(s);
  return Number.isFinite(n) && n > 0 ? n : null;
};

const money = (n, cur = "GBP") => {
  const v = Number(n);
  if (!Number.isFinite(v)) return "—";
  const c = String(cur || "GBP").toUpperCase();
  try {
    return new Intl.NumberFormat("en-GB", { style: "currency", currency: c }).format(v);
  } catch {
    return `${c} ${v.toFixed(2)}`;
  }
};

// Alias used in some UI sections (avoid no-undef build failures)
const formatMoney = money;

const escapeHtml = (s) =>
  String(s ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#039;");

const LINE_ITEMS_TOKEN_START = "<!-- VISION_QUOTE_LINE_ITEMS_START -->";
const LINE_ITEMS_TOKEN_END = "<!-- VISION_QUOTE_LINE_ITEMS_END -->";

const stripLineItemsBlock = (html) => {
  const src = String(html || "");
  const start = src.indexOf(LINE_ITEMS_TOKEN_START);
  const end = src.indexOf(LINE_ITEMS_TOKEN_END);
  if (start === -1 || end === -1 || end <= start) return src;
  return `${src.slice(0, start)}${src.slice(end + LINE_ITEMS_TOKEN_END.length)}`.trim();
};

const nnum = (v, fallback = 0) => {
  const x = Number(v);
  return Number.isFinite(x) ? x : fallback;
};

const lineQty = (li) => nnum(li?.qty ?? li?.quantity ?? 0, 0);
const lineUnitPrice = (li) => nnum(li?.unit_price ?? li?.unitPrice ?? li?.price ?? 0, 0);

// tax_rate in DB is numeric fraction (0.20). If backend returns 20, normalise to 0.20.
const lineTaxRate = (li) => {
  const raw = nnum(li?.tax_rate ?? li?.taxRate ?? 0, 0);
  if (raw > 1.000001) return raw / 100.0;
  return raw < 0 ? 0 : raw;
};

const lineDesc = (li) => norm(li?.name) || norm(li?.description) || "";

const calcLineSubtotal = (li) => lineQty(li) * lineUnitPrice(li);
const calcLineVat = (li) => calcLineSubtotal(li) * lineTaxRate(li);
const calcLineTotal = (li) => {
  const server = nnum(li?.line_total, NaN);
  if (Number.isFinite(server)) return server;
  return calcLineSubtotal(li) + calcLineVat(li);
};

const deriveEmailFromContact = (c) => {
  if (!c) return "";
  const candidates = [c.email, c.email_address, c.primary_email, c.work_email, c.personal_email, c.contact_email];
  for (const v of candidates) {
    if (typeof v === "string" && v.trim()) return v.trim();
  }
  return "";
};

const toArray = (payload) => {
  if (Array.isArray(payload)) return payload;
  if (payload && Array.isArray(payload.items)) return payload.items;
  if (payload && Array.isArray(payload.data)) return payload.data;
  return [];
};

const getAxiosErrInfo = (e) => {
  const status = e?.response?.status ?? null;
  const detail = e?.response?.data?.detail;
  const msg = detail || e?.message || "Request failed.";
  return { status: Number(status) || null, msg: typeof msg === "string" ? msg : JSON.stringify(msg) };
};

const normalizePdfResultToBlob = (result) => {
  if (!result) return null;

  if (typeof Blob !== "undefined" && result instanceof Blob) return result;

  const maybeData = result?.data ?? result?.blob ?? result;

  if (typeof Blob !== "undefined" && maybeData instanceof Blob) return maybeData;

  if (maybeData && maybeData.constructor && maybeData.constructor.name === "ArrayBuffer") {
    return new Blob([maybeData], { type: "application/pdf" });
  }

  if (maybeData && maybeData.constructor && maybeData.constructor.name === "Uint8Array") {
    return new Blob([maybeData], { type: "application/pdf" });
  }

  return null;
};

const normalizeLineItem = (li) => ({
  id: li?.id ?? null,
  tenant_id: li?.tenant_id ?? null,
  quote_id: li?.quote_id ?? null,
  service_id: li?.service_id ?? null,

  catalog_id: li?.catalog_id ?? li?.catalogId ?? li?.catalog?.id ?? null,
  name: String(li?.name || li?.description || "").trim(),
  description: li?.description != null ? String(li.description) : String(li?.name || "").trim(),
  qty: Number(li?.qty ?? li?.quantity ?? 0) || 0,
  quantity: Number(li?.quantity ?? li?.qty ?? 0) || 0,
  unit_price: Number(li?.unit_price ?? li?.unitPrice ?? li?.price ?? 0) || 0,
  unit_cost: li?.unit_cost ?? null,
  tax_rate: li?.tax_rate ?? li?.taxRate ?? 0,

  line_subtotal: li?.line_subtotal ?? null,
  line_tax: li?.line_tax ?? null,
  line_total: li?.line_total ?? null,

  sort_order: li?.sort_order ?? 0,
});

// opportunity helpers --------------------------------------------------------
const getCustomerIdFromOpp = (o) => {
  if (!o || typeof o !== "object") return null;
  const keys = ["customer_id", "customerId", "client_id", "clientId", "account_id", "accountId"];
  for (const k of keys) {
    const n = Number(o[k]);
    if (Number.isFinite(n) && n > 0) return n;
  }
  const nested = o.customer?.id ?? o.client?.id ?? o.account?.id ?? null;
  const nn = Number(nested);
  return Number.isFinite(nn) && nn > 0 ? nn : null;
};

const getPrimaryContactIdFromOpp = (o) => {
  if (!o || typeof o !== "object") return null;
  const keys = ["primary_contact_id", "primaryContactId", "contact_id", "contactId", "raised_by_contact_id", "raisedByContactId"];
  for (const k of keys) {
    const n = Number(o[k]);
    if (Number.isFinite(n) && n > 0) return n;
  }
  const nested = o.contact?.id ?? o.primary_contact?.id ?? null;
  const nn = Number(nested);
  return Number.isFinite(nn) && nn > 0 ? nn : null;
};

const getOppTitle = (o) =>
  norm(o?.title) || norm(o?.name) || norm(o?.summary) || norm(o?.subject) || (o?.id != null ? `Opportunity #${o.id}` : "Opportunity");

const getOppAmount = (o) => {
  const candidates = [o?.amount, o?.value, o?.total, o?.expected_value, o?.expectedValue];
  for (const v of candidates) {
    const n = Number(v);
    if (Number.isFinite(n)) return n;
  }
  return 0;
};

const isOpenOpportunity = (o) => {
  const s = String(o?.status || "").toLowerCase().trim();
  const stage = String(o?.stage || "").toLowerCase().trim();

  const terminal = new Set([
    "won",
    "lost",
    "closed",
    "closed_won",
    "closed_lost",
    "closedwon",
    "closedlost",
    "cancelled",
    "canceled",
    "archived",
  ]);

  if (terminal.has(s)) return false;
  if (terminal.has(stage)) return false;
  if (o?.won_at || o?.lost_at) return false;
  return true;
};

const oppLabel = (o, currency) => {
  const id = o?.id != null ? `#${o.id}` : "#?";
  const title = getOppTitle(o);
  const stage = norm(o?.stage) ? String(o.stage) : "";
  const status = norm(o?.status) ? String(o.status) : "";
  const amt = money(getOppAmount(o), currency);
  const meta = [stage && `Stage: ${stage}`, status && `Status: ${status}`].filter(Boolean).join(" • ");
  return meta ? `${id} — ${title} • ${meta} • ${amt}` : `${id} — ${title} • ${amt}`;
};

// terms helpers --------------------------------------------------------------
const termLabel = (t) => {
  if (!t) return "";
  const v = Number(t?.version);
  const isDef = !!t?.is_default;
  const title = norm(t?.title) || "(untitled)";
  const bits = [];
  if (Number.isFinite(v) && v > 0) bits.push(`v${v}`);
  if (isDef) bits.push("Default");
  return bits.length ? `${title} — ${bits.join(" • ")}` : title;
};

const fetchTermsByKey = async (key, include_inactive = false) => {
  const resp = await axiosInstance.get("/api/terms", { params: { key, include_inactive } });
  return toArray(resp?.data);
};

// IMPORTANT: IDs may be numeric OR UUID. We infer based on loaded docs.
// Returns: { mode: "number"|"string", parse: (raw) => number|string|null, toSelectValue: (id)=>string }
const inferTermsIdMode = (docs) => {
  const arr = Array.isArray(docs) ? docs : [];
  const hasNonNumeric = arr.some((t) => {
    const raw = t?.id;
    if (raw == null) return false;
    const s = String(raw).trim();
    if (!s) return false;
    const n = Number(s);
    return !(Number.isFinite(n) && n > 0);
  });

  const mode = hasNonNumeric ? "string" : "number";

  const parse = (raw) => {
    const s = String(raw ?? "").trim();
    if (!s) return null;
    if (mode === "number") {
      const n = Number(s);
      return Number.isFinite(n) && n > 0 ? n : null;
    }
    return s;
  };

  const toSelectValue = (id) => {
    if (id == null) return "";
    const s = String(id).trim();
    return s || "";
  };

  return { mode, parse, toSelectValue };
};

// VAT input helpers ----------------------------------------------------------
// UI works in percent, DB/API expects fraction (0.20). Keep it idiot-proof.
const pctToRate = (pct) => {
  const p = nnum(pct, 0);
  if (p <= 0) return 0;
  // allow "0.2" to be entered by mistake; treat <=1 as fraction already
  if (p > 0 && p <= 1) return p;
  return p / 100.0;
};

const rateToPct = (rate) => {
  const r = nnum(rate, 0);
  if (r <= 0) return 0;
  if (r > 1.000001) return r; // already a percent
  return r * 100.0;
};

// ✅ FIX (2026-02-16): Default VAT to 20% when a *catalog/pricebook item* has no tax field.
// IMPORTANT: preserve explicit zero-rated items (0 / "0" / 0.0 must remain 0).
const DEFAULT_VAT_RATE = 0.2; // 20%
const resolveCatalogVatRate = (it) => {
  if (!it || typeof it !== "object") return DEFAULT_VAT_RATE;

  const raw =
    it?.tax_rate ??
    it?.taxRate ??
    it?.vat_rate ??
    it?.vatRate ??
    it?.vat ??
    it?.tax ??
    null;

  // Treat null/undefined/"" as "missing" => default 20%
  if (raw == null) return DEFAULT_VAT_RATE;
  if (typeof raw === "string" && !raw.trim()) return DEFAULT_VAT_RATE;

  // Explicit 0 must stay 0
  const n = Number(raw);
  if (Number.isFinite(n) && n === 0) return 0;

  // Otherwise normalise like lineTaxRate (supports 20 => 0.20)
  return lineTaxRate({ tax_rate: n });
};

// Quote status helpers -------------------------------------------------------
// (3) Lock editing when status != draft/submitted (mirrors DB rules).
const normStatus = (s) => String(s ?? "").trim().toLowerCase();
const isDraftOrSubmitted = (s) => {
  const v = normStatus(s);
  return v === "draft" || v === "submitted";
};

// pricebook helpers ----------------------------------------------------------
const pbLabel = (pb) => {
  if (!pb) return "Pricebook";

  // ✅ backend uses `label` as the human name
  const name =
    norm(pb?.label) ||
    norm(pb?.name) ||
    norm(pb?.title) ||
    `Pricebook #${pb?.id ?? "?"}`;

  const scope =
    pb?.scope ||
    pb?.availability ||
    pb?.visibility ||
    (pb?.customer_id ? "customer" : pb?.resource_id ? "resource" : "tenant");

  const isDef = !!pb?.is_default;

  const bits = [];
  if (scope) bits.push(String(scope));
  if (isDef) bits.push("default");

  return bits.length ? `${name} — ${bits.join(" • ")}` : name;
};

const pbItemLabel = (it) => norm(it?.name) || norm(it?.description) || `Item #${it?.id ?? "?"}`;

// ---- NEW: pricebook scoping (customer/resource/tenant-wide) ----------------
const pickScopedPricebooks = ({ all, customerId, resourceId }) => {
  const cid = toIntOrNull(customerId);
  const rid = toIntOrNull(resourceId);

  const arr = Array.isArray(all) ? all : [];
  const active = arr.filter((pb) => pb && pb.active === true);

  const customerScoped = cid ? active.filter((pb) => Number(pb?.customer_id) === Number(cid)) : [];
  const resourceScoped = rid ? active.filter((pb) => Number(pb?.resource_id) === Number(rid)) : [];
  const tenantWide = active.filter((pb) => pb?.customer_id == null && pb?.resource_id == null);

  // priority: customer -> resource -> tenant
  const seen = new Set();
  const merged = [];
  for (const pb of [...customerScoped, ...resourceScoped, ...tenantWide]) {
    const id = toIntOrNull(pb?.id);
    if (!id || seen.has(id)) continue;
    seen.add(id);
    merged.push(pb);
  }

  // display sort: default first, then label
  merged.sort((a, b) => {
    const ad = a?.is_default ? 1 : 0;
    const bd = b?.is_default ? 1 : 0;
    if (ad !== bd) return bd - ad;
    return String(pbLabel(a)).localeCompare(String(pbLabel(b)));
  });

  return merged;
};

export default function QuoteCreateModal({
  open,
  onClose,
  onCreate,
  onQuoteChanged,

  customerMap,
  theme: themeProp,

  defaultCustomerId = "",
  defaultOpportunityId = "",

  contextQuoteId = null,
  quoteId: quoteIdProp = null,

  contextOpportunityId = null,

  onAlert,
  onLoadContacts,
  contactLabel = (c) => {
    const name = c?.name || [c?.first_name, c?.last_name].filter(Boolean).join(" ").trim() || `Contact #${c?.id}`;
    const email = deriveEmailFromContact(c);
    return email ? `${name} — ${email}` : name;
  },

  // NOTE: onSearchCatalog removed (dropdown-based pricebook selection only)

  onPreviewPdf,

  defaultBodyHtml = "",
}) {
  const themeCtx = useTheme?.() || {};
  const theme = themeProp || themeCtx?.theme || themeCtx || {};

  const [quoteId, setQuoteId] = useState(null);
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [lastSavedAt, setLastSavedAt] = useState(null);
  const [lastPreviewAt, setLastPreviewAt] = useState(null);

  const [previewNonce, setPreviewNonce] = useState(0);

  const [customerId, setCustomerId] = useState("");
  const [opportunityId, setOpportunityId] = useState("");
  const [currency, setCurrency] = useState("GBP");

  // Money formatter alias used by the multi-option UI (keeps older call-sites working).
  const formatMoney = useCallback((value) => money(value, currency), [currency]);


  // Title (ADD)
  const [quoteTitle, setQuoteTitle] = useState("");

  const [contactId, setContactId] = useState("");
  const [contacts, setContacts] = useState([]);
  const [contactsBusy, setContactsBusy] = useState(false);

  const [toEmail, setToEmail] = useState("");

  const [bodyHtml, setBodyHtml] = useState("");
  const [specificNotes, setSpecificNotes] = useState("");
  // Reusable notes are copied into the quote HTML (no live library linkage).
  const [noteLibrary, setNoteLibrary] = useState([]);
  const [noteLibraryOpen, setNoteLibraryOpen] = useState(false);
  const [noteLibrarySelection, setNoteLibrarySelection] = useState([]);
  const [noteLibraryBusy, setNoteLibraryBusy] = useState(false);
  const [noteLibraryError, setNoteLibraryError] = useState("");
  const loadNoteLibrary = useCallback(async () => {
    setNoteLibraryBusy(true);
    setNoteLibraryError("");
    try {
      const response = await axiosInstance.get("/api/quote-note-snippets");
      setNoteLibrary(Array.isArray(response.data) ? response.data : []);
    } catch (err) {
      setNoteLibraryError(err?.response?.data?.detail || err?.message || "Unable to load reusable notes");
    } finally { setNoteLibraryBusy(false); }
  }, []);



// Quote images (2 slots) — backend endpoints may not be enabled yet; UI must not crash.
const [quoteImagesSupported, setQuoteImagesSupported] = useState("unknown"); // unknown | ok | unsupported
const [quoteImages, setQuoteImages] = useState({}); // { [slot]: { file_url, caption } }
const [imgDraft, setImgDraft] = useState({}); // { [slot]: { file: File, previewUrl, caption } }
	const [imagesTouchedAt, setImagesTouchedAt] = useState(null); // used to mark preview stale when images change

	const apiBaseUrl = useMemo(() => {
		const raw = axiosInstance?.defaults?.baseURL || "";
		if (typeof raw !== "string") return "";
		let v = raw.trim().replace(/\/$/, "");
		// If baseURL includes /api, strip it so /static resolves to backend origin.
		v = v.replace(/\/api\/?$/, "");
		// If baseURL is relative (e.g. "/api"), this yields "".
		return v;
	}, []);

  // NOTE:
  // NOTE:
  //   Quote image URLs must be resolvable by the *end-user browser*.
  //   If the API base URL is loopback/private (localhost/127.0.0.1/::1 or RFC1918), it only works on the server machine.
  //   In that case, fall back to the current page origin (the real host the user loaded).
  const isLoopbackHost = (host) => {
    if (!host) return false;
    const h = String(host).trim().toLowerCase();
    if (
      h === "localhost" ||
      h === "127.0.0.1" ||
      h === "::1" ||
      h.endsWith(".localhost") ||
      /^127\.(?:\d{1,3}\.){2}\d{1,3}$/.test(h)
    ) {
      return true;
    }
    // RFC1918 private ranges (common in docker / reverse-proxy setups).
    // These are not reachable from an external client browser, so treat them as loopback-ish.
    if (/^10\.(?:\d{1,3}\.){2}\d{1,3}$/.test(h)) return true;
    if (/^192\.168\.(?:\d{1,3})\.(?:\d{1,3})$/.test(h)) return true;
    const m = h.match(/^172\.(\d{1,2})\.(?:\d{1,3})\.(?:\d{1,3})$/);
    if (m) {
      const second = Number(m[1]);
      if (second >= 16 && second <= 31) return true;
    }
    return false;
  };

	const staticBaseUrl = useMemo(() => {
		// Priority:
		//  1) absolute axios baseURL (without trailing /api)
		//  2) build-time env override (for dev/proxy setups where /static is not proxied)
		//  3) window origin (works when reverse-proxy serves /static on same origin)
		let base = apiBaseUrl;

		if (!base) {
				// IMPORTANT: do not reference `process` directly in runtime code.
				// In some builds, `process` may be undefined which would crash the page.
				const envObj = (typeof process !== "undefined" && process && process.env) ? process.env : {};
				const env =
					(envObj.REACT_APP_API_BASE_URL || "") ||
					(envObj.REACT_APP_API_URL || "") ||
					(envObj.REACT_APP_BACKEND_URL || "") ||
					(envObj.REACT_APP_BACKEND_BASE_URL || "") ||
					"";
			if (typeof env === "string" && env.trim()) {
				base = env.trim().replace(/\/$/, "").replace(/\/api\/?$/, "");
			}
		}

		if (!base && typeof window !== "undefined" && window?.location?.origin) {
			base = window.location.origin;
		}

		// If the API base URL points at loopback (e.g. REACT_APP_API_BASE_URL=http://localhost:8010)
// then the backend may generate absolute static URLs like:
// http://localhost:8010/static/... which only works on the server, not on the end-user machine.
//
// Rules:
// 1) If we're running under a public hostname (e.g. vts.paulcastle.com), always serve static from window.origin.
// 2) If we're running locally on the server via http://localhost:8010 (nginx frontend) and the backend is on :8020,
//    serve static from :8020 so quote image previews work without needing proxy rules.
//    (We ONLY apply this rewrite for the known frontend port 8010 to avoid surprise in other environments.)
try {
	const winHost = window.location.hostname;
	const winPort = window.location.port || (window.location.protocol === "https:" ? "443" : "80");
	const isWinLoopback = isLoopbackHost(winHost);

	// If NOT loopback, force origin to the page origin.
	if (!isWinLoopback) {
		base = window.location.origin;
	} else {
		// Loopback page origin (local testing).
		// If the app is served on :8010 and the backend is on :8020, use :8020 for /static/quotes.
		// This keeps local server testing working while still avoiding broken end-user URLs.
		if (winPort === "8010") {
			base = `${window.location.protocol}//${winHost}:8020`;
		}
	}
} catch (_) {
	// ignore URL parsing failures (e.g. relative base)
}


		return base || "";
	}, [apiBaseUrl]);

	const resolveStaticUrl = useCallback(

		(url) => {
			const u = typeof url === "string" ? url.trim() : "";
			if (!u) return "";
			if (/^https?:\/\//i.test(u)) {
				// Defend against loopback absolute URLs persisted by the backend (e.g.
				// http://localhost:8010/static/...) which work only on the server machine.
				try {
					const urlObj = new URL(u);
					const winHost = window?.location?.hostname;
					if (isLoopbackHost(urlObj.hostname) && !isLoopbackHost(winHost) && window?.location?.origin) {
						return `${window.location.origin}${urlObj.pathname}${urlObj.search}${urlObj.hash}`;
					}
					return u;
				} catch {
					return u;
				}
			}
			if (u.startsWith("/")) {
				// In dev the frontend origin differs from backend; /static must be resolved against API base.
				return staticBaseUrl ? `${staticBaseUrl}${u}` : u;
			}
			return u;
		},
		[staticBaseUrl]
	);

const setQuoteImageCaption = (slot, caption) => {
  setImgDraft((prev) => ({ ...(prev || {}), [slot]: { ...((prev || {})[slot] || {}), caption } }));

  // Caption edits are part of the quote's normal unsaved-change lifecycle.
  // Set the main dirty flag immediately so Save and Save & Refresh enable
  // as soon as the user types, changes, or clears a caption.
  if (open && quoteId && !quoteLocked) {
    setDirty(true);
  }
};

const pickQuoteImage = (slot, file) => {
  if (!slot) return;
  setImgDraft((prev) => {
    const next = { ...(prev || {}) };
    const existing = next[slot] || {};
    if (existing?.previewUrl) {
      try { URL.revokeObjectURL(existing.previewUrl); } catch {}
    }
    if (!file) {
      next[slot] = { ...existing, file: null, previewUrl: "" };
      return next;
    }
    const previewUrl = URL.createObjectURL(file);
    next[slot] = { ...existing, file, previewUrl };
    return next;
  });
};

	const loadQuoteImages = async (qid, opts = {}) => {
  const id = toIntOrNull(qid);
  if (!id) return;
  try {
    const res = await axiosInstance.get(`/api/quotes/${id}/images`);
    const rows = Array.isArray(res?.data)
      ? res.data
      : Array.isArray(res?.data?.items)
      ? res.data.items
      : [];
			const map = {};
			const nonce = opts?.bust ? Date.now() : null;
    for (const r of rows) {
      const slot = Number(r?.slot || 0);
				if (slot === 1 || slot === 2) map[slot] = nonce ? { ...(r || {}), _nonce: nonce } : r;
    }
    setQuoteImages(map);
    setQuoteImagesSupported("ok");
  } catch (e) {
    const info = getAxiosErrInfo(e);
    if (info?.status === 404) setQuoteImagesSupported("unsupported");
    else setQuoteImagesSupported("unsupported");
  }
};

const clearSavedQuoteImageCaptionDraft = (slot) => {
  setImgDraft((prev) => {
    const current = (prev || {})[slot];
    if (!current || typeof current.caption !== "string") return prev;

    const next = { ...(prev || {}) };
    const cleaned = { ...current };
    delete cleaned.caption;

    if (!cleaned.file && !cleaned.previewUrl) delete next[slot];
    else next[slot] = cleaned;

    return next;
  });
};

const fetchStoredQuoteImageBlob = async (slot) => {
  const existing = quoteImages?.[slot];
  const rawUrl = existing?.file_url || existing?.fileUrl || "";
  const resolvedUrl = rawUrl ? resolveStaticUrl(rawUrl) : "";

  if (!resolvedUrl) {
    throw new Error(`Image ${slot} has no stored file URL.`);
  }

  const response = await fetch(resolvedUrl, {
    method: "GET",
    cache: "no-store",
    credentials: "same-origin",
  });

  if (!response.ok) {
    throw new Error(`Unable to load existing image ${slot} (${response.status}).`);
  }

  const blob = await response.blob();
  if (!blob || !blob.size) {
    throw new Error(`Existing image ${slot} is empty.`);
  }

  let filename = `quote-${quoteId}-image-${slot}`;
  try {
    const parsed = new URL(resolvedUrl, window.location.href);
    const lastPart = parsed.pathname.split("/").filter(Boolean).pop();
    if (lastPart) filename = decodeURIComponent(lastPart);
  } catch {}

  return { blob, filename };
};

const postQuoteImage = async (slot, fileOrBlob, caption, filename = "") => {
  const id = toIntOrNull(quoteId);
  if (!id) throw new Error("Missing quote id.");
  if (!fileOrBlob) throw new Error(`Image ${slot} has no file to save.`);

  const form = new FormData();
  if (filename) form.append("file", fileOrBlob, filename);
  else form.append("file", fileOrBlob);
  form.append("caption", typeof caption === "string" ? caption : "");

  // IMPORTANT: do not force Content-Type; axios must set multipart boundary.
  await axiosInstance.post(`/api/quotes/${id}/images/${slot}`, form);
};

const saveExistingQuoteImageCaption = async (slot) => {
  const id = toIntOrNull(quoteId);
  if (!id) throw new Error("Missing quote id.");

  const existing = quoteImages?.[slot];
  const draft = imgDraft?.[slot] || {};

  if (!existing || typeof draft.caption !== "string") return false;

  const currentCaption = String(existing?.caption ?? "");
  if (draft.caption === currentCaption) return false;

  const stored = await fetchStoredQuoteImageBlob(slot);
  await postQuoteImage(slot, stored.blob, draft.caption, stored.filename);

  setImagesTouchedAt(Date.now());
  await loadQuoteImages(id, { bust: true });
  clearSavedQuoteImageCaptionDraft(slot);
  return true;
};

	// Auto-detect whether the backend image endpoints are enabled and load images when a quote exists.
	useEffect(() => {
	  if (!quoteId) return;
	  loadQuoteImages(quoteId);
	  // eslint-disable-next-line react-hooks/exhaustive-deps
	}, [quoteId]);

const uploadQuoteImage = async (slot) => {
  if (quoteImagesSupported !== "ok") return;
  if (!quoteId) return;
  const id = toIntOrNull(quoteId);
  if (!id) return;

  const draft = imgDraft?.[slot] || {};
  const existing = quoteImages?.[slot];
  const caption = typeof draft.caption === "string" ? draft.caption : String(existing?.caption ?? "");
  const captionChanged = !!existing && caption !== String(existing?.caption ?? "");

  try {
    if (draft?.file) {
      await postQuoteImage(slot, draft.file, caption);
    } else if (existing && captionChanged) {
      const stored = await fetchStoredQuoteImageBlob(slot);
      await postQuoteImage(slot, stored.blob, caption, stored.filename);
    } else {
      onAlert?.("warning", existing ? "No image or caption changes to save." : "Select an image first.");
      return;
    }

    setImagesTouchedAt(Date.now());
    await loadQuoteImages(id, { bust: true });

    setImgDraft((prev) => {
      const next = { ...(prev || {}) };
      const current = next[slot] || {};
      if (current?.previewUrl) {
        try { URL.revokeObjectURL(current.previewUrl); } catch {}
      }
      delete next[slot];
      return next;
    });

    onAlert?.("success", draft?.file ? `Image ${slot} uploaded.` : `Image ${slot} caption saved.`);
  } catch (e) {
    const info = getAxiosErrInfo(e);
    onAlert?.("warning", info.msg);
  }
};

const deleteQuoteImage = async (slot) => {
  if (quoteImagesSupported !== "ok") return;
  if (!quoteId) return;
  const id = toIntOrNull(quoteId);
  if (!id) return;

  try {
    await axiosInstance.delete(`/api/quotes/${id}/images/${slot}`);
			// Optimistically remove from UI immediately.
			setQuoteImages((prev) => {
				const next = { ...(prev || {}) };
				delete next[slot];
				return next;
			});
			setImgDraft((prev) => {
				const next = { ...(prev || {}) };
				const existingDraft = next[slot] || {};
				if (existingDraft?.previewUrl) {
					try { URL.revokeObjectURL(existingDraft.previewUrl); } catch {}
				}
				delete next[slot];
				return next;
			});
			setImagesTouchedAt(Date.now());
			await loadQuoteImages(id, { bust: true });
    onAlert?.("success", `Image ${slot} removed.`);
  } catch (e) {
    const info = getAxiosErrInfo(e);
    onAlert?.("warning", info.msg);
  }
};


  // line items (server-backed once quoteId exists)
  const [lineItems, setLineItems] = useState([]);
  const [quoteOptions, setQuoteOptions] = useState([]); // [{id, option_key, label, sort_order, option_total...}]
  const [selectedOptionId, setSelectedOptionId] = useState(null);
  const selectedOptionIdRef = useRef(null);
  useEffect(() => {
    selectedOptionIdRef.current = selectedOptionId;
  }, [selectedOptionId]);
  const [optionLabelDraft, setOptionLabelDraft] = useState("");

  const [optionLineItems, setOptionLineItems] = useState([]);
  const [optionLineItemsByOptionId, setOptionLineItemsByOptionId] = useState({});
  const optionLineItemsByOptionIdRef = useRef({});
  useEffect(() => {
    optionLineItemsByOptionIdRef.current = optionLineItemsByOptionId || {};
  }, [optionLineItemsByOptionId]);

  const [optLiDescription, setOptLiDescription] = useState("");
  const [optLiQty, setOptLiQty] = useState(1);
  const [optLiUnitPrice, setOptLiUnitPrice] = useState(0);
  const [optLiVatRate, setOptLiVatRate] = useState(0.2);
  const [optLiUnitCost, setOptLiUnitCost] = useState("");
  // FIX 1: consistent name — was referenced as optLiSort / setOptLiSort in JSX (undefined)
  const [optLiSaving, setOptLiSaving] = useState(false);
  const [optLiError, setOptLiError] = useState("");
  const [liMeta, setLiMeta] = useState(null);
  const [liBusy, setLiBusy] = useState(false);
  const [liErr, setLiErr] = useState("");
  const [approverOptions, setApproverOptions] = useState([]);
  const [approverErr, setApproverErr] = useState("");
  const [liCommitBusyId, setLiCommitBusyId] = useState(null);
  const [liDraftAdding, setLiDraftAdding] = useState(false);

  // (3) quote status for edit-lock (defensive: from quote payload + status/meta)
  const [quoteStatus, setQuoteStatus] = useState("");

  // ✅ NEW (UI only for now): Multi-option toggle state
  // IMPORTANT: This ONLY controls UI visibility for now. It does not change payloads yet.
  const [multiOptionEnabled, setMultiOptionEnabled] = useState(false);
  const [showUnitBreakdown, setShowUnitBreakdown] = useState(false);

  // manual line item entry (MATCH widget behaviour)
  const [manualDesc, setManualDesc] = useState("");
  const [manualQty, setManualQty] = useState("1");
  const [manualUnitPrice, setManualUnitPrice] = useState("");
  const [manualVatPct, setManualVatPct] = useState("20");

  // pricebooks + items (dropdown-driven)
  const [pricebooksBusy, setPricebooksBusy] = useState(false);
  const [pricebooksErr, setPricebooksErr] = useState("");
  const [pricebooks, setPricebooks] = useState([]); // raw list from API (active tenant scope)
  const [selectedPricebookId, setSelectedPricebookId] = useState("");

  const [pbItemsBusy, setPbItemsBusy] = useState(false);
  const [pbItemsErr, setPbItemsErr] = useState("");
  // FIX 2: this is the canonical state variable — was wrongly referenced as `pricebookItems` in JSX
  const [pbItems, setPbItems] = useState([]);

  const [pdfUrl, setPdfUrl] = useState("");
  const [pdfErr, setPdfErr] = useState("");

  const [saveErr, setSaveErr] = useState("");

  const [pdfTriedPath, setPdfTriedPath] = useState("");
  const [pdfTriedStatus, setPdfTriedStatus] = useState(null);

  // Mobile-only workspace state. Desktop ignores these through lg: display classes.
  const [mobileWorkspace, setMobileWorkspace] = useState("details"); // details | items | preview | more
  const [mobileItemsPane, setMobileItemsPane] = useState("entry"); // entry | pricebook | current
  const [mobileImageSlot, setMobileImageSlot] = useState(1);

  useEffect(() => {
    if (!open) return;
    setMobileWorkspace("details");
    setMobileItemsPane("entry");
    setMobileImageSlot(1);
  }, [open, contextQuoteId, quoteIdProp]);

  const [oppsBusy, setOppsBusy] = useState(false);
  const [allOpps, setAllOpps] = useState([]);
  const [rawOppsCount, setRawOppsCount] = useState(0);
  const [lastOppsFetchErr, setLastOppsFetchErr] = useState("");

  // terms dropdowns
  const [quoteTermsBusy, setQuoteTermsBusy] = useState(false);
  const [serviceTermsBusy, setServiceTermsBusy] = useState(false);
  const [quoteTermsErr, setQuoteTermsErr] = useState("");
  const [serviceTermsErr, setServiceTermsErr] = useState("");
  const [quoteTermsDocs, setQuoteTermsDocs] = useState([]);
  const [serviceTermsDocs, setServiceTermsDocs] = useState([]);

  // IMPORTANT: store IDs as (number|string|null), NEVER labels.
  const [quoteTermsDocumentId, setQuoteTermsDocumentId] = useState(null);
  const [serviceTermsDocumentId, setServiceTermsDocumentId] = useState(null);

  // infer id mode from loaded docs (number vs uuid string)
  const quoteTermsIdMode = useMemo(() => inferTermsIdMode(quoteTermsDocs), [quoteTermsDocs]);
  const serviceTermsIdMode = useMemo(() => inferTermsIdMode(serviceTermsDocs), [serviceTermsDocs]);

  const pdfUrlRef = useRef("");
  useEffect(() => {
    pdfUrlRef.current = pdfUrl;
  }, [pdfUrl]);

  const customers = useMemo(() => {
    const entries = Object.entries(customerMap || {});
    entries.sort((a, b) => String(a[1] || "").localeCompare(String(b[1] || "")));
    return entries.map(([id, name]) => ({ id: String(id), name: String(name || "") }));
  }, [customerMap]);

  const effectiveOpportunityId = useMemo(() => {
    const v = norm(opportunityId) || norm(contextOpportunityId) || norm(defaultOpportunityId);
    return v;
  }, [opportunityId, contextOpportunityId, defaultOpportunityId]);

  const headerBadges = useMemo(() => {
    const qid = quoteId != null ? String(quoteId) : "";
    const oid = norm(effectiveOpportunityId);
    const badges = [];
    if (qid) badges.push({ k: "Quote", v: `#${qid}` });
    if (oid) badges.push({ k: "Opportunity", v: `#${oid}` });
    if (qid) {
      const s = normStatus(quoteStatus);
      if (s) badges.push({ k: "Status", v: s });
    }
    return badges;
  }, [quoteId, effectiveOpportunityId, quoteStatus]);

  const imageCaptionDirtySlots = useMemo(() => {
    return [1, 2].filter((slot) => {
      const existing = quoteImages?.[slot];
      const draft = imgDraft?.[slot] || {};

      // A newly selected file is still governed by the explicit per-image Upload action.
      // Save / Save & Refresh only own caption-only edits to an already stored image.
      if (!existing || draft?.file || typeof draft.caption !== "string") return false;

      return draft.caption !== String(existing?.caption ?? "");
    });
  }, [imgDraft, quoteImages]);

  const imageCaptionDirty = imageCaptionDirtySlots.length > 0;

	const previewStale = useMemo(() => {
		if (!quoteId) return false;
		if (dirty || imageCaptionDirty) return true;
		if (imagesTouchedAt && (!lastPreviewAt || Number(imagesTouchedAt) > Number(lastPreviewAt))) return true;
		if (!lastSavedAt || !lastPreviewAt) return false;
		return Number(lastSavedAt) > Number(lastPreviewAt);
	}, [quoteId, dirty, imageCaptionDirty, imagesTouchedAt, lastSavedAt, lastPreviewAt]);

  const canPreview = !!quoteId;

  const fontBody = theme?.font_body || "system-ui";
  const fontHeader = theme?.font_header || theme?.font_body || "system-ui";

  const labelCls = "block text-[11px] text-gray-600 mb-1 font-semibold min-h-[16px]";
  const fieldCls = "w-full min-w-0 border border-gray-300 rounded px-3 py-2 text-xs bg-white";

  // (3) hard lock rule (mirrors DB governance): only draft/submitted editable
  const quoteLocked = useMemo(() => {
    if (!quoteId) return false; // create mode
    // If we haven't resolved status yet, do NOT hard-lock (avoid accidental UX freeze).
    // Once status is known, enforce strictly.
    if (!normStatus(quoteStatus)) return false;
    return !isDraftOrSubmitted(quoteStatus);
  }, [quoteId, quoteStatus]);

  const markDirty = () => {
    if (!open) return;
    if (!quoteId) return;
    if (quoteLocked) return;
    setDirty(true);
  };

  const markDirtyIfEditing = () => {
    if (!open) return;
    if (!quoteId) return; // create mode: no dirty gating required
    if (quoteLocked) return;
    setDirty(true);
  };

  // --- Line Items API (same contract as QuoteLineItemsWidget) ---------------
  const liEditable = Boolean(liMeta?.line_items_editable) && !quoteLocked;

  const loadLineItems = useCallback(async (qid) => {
    const id = toIntOrNull(qid);
    if (!id) {
      setLineItems([]);
      setLiMeta(null);
      setLiErr("");
      return;
    }

    setLiBusy(true);
    setLiErr("");
    try {
      const [iRes, mRes] = await Promise.all([
        axiosInstance.get(`/api/quotes/${id}/line-items`),
        axiosInstance.get(`/api/quotes/${id}/status/meta`),
      ]);

      const items = toArray(iRes?.data).map(normalizeLineItem);
      setLineItems(Array.isArray(items) ? items : []);
      const meta = mRes?.data || null;
      setLiMeta(meta);

      // Approvers (used by NextStepCard submit workflow). Do not fail line item load if this endpoint fails.
      try {
        const aRes = await axiosInstance.get(`/api/quotes/approvers/me`);
        setApproverOptions(Array.isArray(aRes?.data) ? aRes.data : []);
        setApproverErr("");
      } catch (e) {
        setApproverOptions([]);
        const msg = e?.response?.data?.detail || e?.message || "Failed to load approvers.";
        setApproverErr(String(msg));
      }

      // (3) status: derive defensively from meta if present
      const metaStatus =
        meta?.status ??
        meta?.current_status ??
        meta?.quote_status ??
        meta?.quote?.status ??
        meta?.quote?.current_status ??
        null;
      if (metaStatus != null && String(metaStatus).trim()) setQuoteStatus(String(metaStatus));
    } catch (e) {
      const info = getAxiosErrInfo(e);
      setLineItems([]);
      setLiMeta(null);
      setLiErr(`Failed to load line items${info.status ? ` (${info.status})` : ""}: ${info.msg}`);
    } finally {
      setLiBusy(false);
    }
  }, []);

  const computeOptionTotals = useCallback((items) => {
    const safeNum = (v) => {
      const n = Number(v);
      return Number.isFinite(n) ? n : 0;
    };

    let subtotal = 0;
    let tax = 0;

    for (const li of items || []) {
      const qty = safeNum(li.quantity ?? li.qty ?? 0);
      const unit = safeNum(li.unit_price ?? li.unitPrice ?? 0);

      // tax_rate can come back as 0.2, 20, "0.2", "20", or null.
      let rate = safeNum(li.tax_rate ?? li.taxRate ?? li.vat_rate ?? li.vatRate ?? 0);
      if (rate > 1) rate = rate / 100;

      const lineSubtotal = qty * unit;
      const lineTax = lineSubtotal * rate;

      subtotal += lineSubtotal;
      tax += lineTax;
    }

    const total = subtotal + tax;

    return {
      subtotal,
      tax,
      total,
      grand_total: total,
      currency: currency || "GBP",
    };
  }, [currency]);

  const getOptionTotals = useCallback(
    (optionId) => computeOptionTotals(optionLineItemsByOptionId?.[optionId] || []),
    [computeOptionTotals, optionLineItemsByOptionId]
  );

const apiCreateLineItem = useCallback(async (qid, payload) => {
    const id = toIntOrNull(qid);
    if (!id) throw new Error("Missing quote id.");

    const safe = {
      description: String(payload?.description || "").trim(),
      quantity: Number(payload?.quantity ?? 1) || 1,
      unit_price: Number(payload?.unit_price ?? 0) || 0,
      tax_rate: Number(payload?.tax_rate ?? 0) || 0,
      sort_order: Number(payload?.sort_order ?? 0) || 0,
      service_id: payload?.service_id ?? null,
    };

    if (!safe.description) {
      throw new Error("description is required");
    }

    const resp = await axiosInstance.post(`/api/quotes/${id}/line-items`, safe);
    return resp?.data || null;
  }, []);

  const apiPatchLineItem = useCallback(async (qid, lineItemId, updates) => {
    const id = toIntOrNull(qid);
    const liId = toIntOrNull(lineItemId);
    if (!id || !liId) throw new Error("Missing ids.");

    const safe = { ...updates };

    if (safe.qty != null && safe.quantity == null) safe.quantity = Number(safe.qty) || 0;
    if (safe.name != null && safe.description == null) safe.description = String(safe.name || "").trim();

    if (safe.quantity != null) safe.quantity = Number(safe.quantity) || 0;
    if (safe.unit_price != null) safe.unit_price = Number(safe.unit_price) || 0;

    if (safe.tax_rate != null) {
      const r = Number(safe.tax_rate) || 0;
      safe.tax_rate = r > 1.000001 ? r / 100.0 : r;
      if (safe.tax_rate < 0) safe.tax_rate = 0;
    }

    if (safe.description != null) safe.description = String(safe.description || "").trim();

    const resp = await axiosInstance.patch(`/api/quotes/${id}/line-items/${liId}`, safe);
    return resp?.data || null;
  }, []);

  const apiDeleteLineItem = useCallback(async (qid, lineItemId) => {
    const id = toIntOrNull(qid);
    const liId = toIntOrNull(lineItemId);
    if (!id || !liId) throw new Error("Missing ids.");

    await axiosInstance.delete(`/api/quotes/${id}/line-items/${liId}`);
    return true;
  }, []);

  useEffect(() => {
    if (!open) return;
    if (!quoteId) {
      setLiMeta(null);
      setLiErr("");
      return;
    }
    loadLineItems(quoteId);
  }, [open, quoteId, loadLineItems]);

  // --- pricebooks (dropdown-driven) ----------------------------------------
  const loadEligiblePricebooks = useCallback(async () => {
    setPricebooksErr("");
    setPricebooksBusy(true);
    try {
      // ✅ IMPORTANT:
      // Do NOT call /eligible here: it requires server-side context (customer/resource) we are not passing.
      // Fetch active pricebooks list (matches your curl) and filter client-side by customer/resource/tenant-wide.
      const resp = await axiosInstance.get(`${PRICEBOOKS_BASE}`, { params: { include_inactive: false } });
      const rows = toArray(resp?.data);
      const arr = Array.isArray(rows) ? rows : [];

      setPricebooks(arr);
    } catch (e) {
      const info = getAxiosErrInfo(e);
      setPricebooks([]);
      setSelectedPricebookId("");
      setPricebooksErr(`Failed to load pricebooks${info.status ? ` (${info.status})` : ""}: ${info.msg}`);
    } finally {
      setPricebooksBusy(false);
    }
  }, []);

  // ✅ derived eligible list (customer-scoped + resource-scoped + tenant-wide)
  // NOTE: we don't currently have resource context in this modal; keep null for now.
  const eligiblePricebooks = useMemo(() => {
    return pickScopedPricebooks({
      all: pricebooks,
      customerId,
      resourceId: null,
    });
  }, [pricebooks, customerId]);

  // ✅ keep/clear selection when eligibility changes (customer change, reload, etc.)
  useEffect(() => {
    if (!open) return;

    setSelectedPricebookId((prev) => {
      const cur = norm(prev);
      if (!cur) return "";
      const ok = eligiblePricebooks.some((x) => String(x?.id ?? "") === cur);
      return ok ? cur : "";
    });

    // if selection is cleared, clear items too
    if (selectedPricebookId && !eligiblePricebooks.some((x) => String(x?.id ?? "") === String(selectedPricebookId))) {
      setPbItems([]);
      setPbItemsErr("");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, customerId, eligiblePricebooks]);

  // ─────────────────────────────────────────────
  // Multi-option Quote: Options + Option Line Items (A/B/C)
  // ─────────────────────────────────────────────
  const loadQuoteOptions = useCallback(
    async (qid) => {
      const id = qid || quoteId;
      if (!id || !axiosInstance) return [];
      try {
        const res = await axiosInstance.get(`/api/quotes/${id}/options`);
        const opts = Array.isArray(res?.data) ? res.data : [];
        setQuoteOptions(opts);

        // Maintain a valid selected option.
        const existingIds = new Set(opts.map((o) => o?.id).filter((v) => v != null));
        setSelectedOptionId((prev) => {
          if (prev && existingIds.has(prev)) return prev;
          return opts.length ? opts[0].id : null;
        });

        return opts;
      } catch (e) {
        console.error("Failed to load quote options:", e);
        setQuoteOptions([]);
        setSelectedOptionId(null);
        return [];
      }
    },
    [axiosInstance, quoteId]
  );

  const ensureDefaultOptionsABC = useCallback(
    async (qid) => {
      const id = qid || quoteId;
      if (!id || !axiosInstance) return;
      try {
        const existing = await loadQuoteOptions(id);
        const keys = new Set((existing || []).map((o) => String(o?.option_key || "").toUpperCase()));
        const desired = [
          { option_key: "A", label: "Option A", sort_order: 0 },
          { option_key: "B", label: "Option B", sort_order: 1 },
          { option_key: "C", label: "Option C", sort_order: 2 },
        ];

        // Create only missing keys; backend enforces max 3 + uniqueness.
        for (const d of desired) {
          if (!keys.has(d.option_key)) {
            try {
              await axiosInstance.post(`/api/quotes/${id}/options`, d);
            } catch (e) {
              // Ignore duplicates/max-3 errors; we just want best effort.
            }
          }
        }

        await loadQuoteOptions(id);
      } catch (e) {
        console.error("Failed to ensure default options:", e);
      }
    },
    [axiosInstance, quoteId, loadQuoteOptions]
  );

  const loadOptionLineItems = useCallback(
    async (qid, optionId, opts = {}) => {
      const id = qid || quoteId;
      if (!id || !optionId || !axiosInstance) return [];

      if (!opts.prefetch) {
        const cached = optionLineItemsByOptionIdRef.current?.[optionId];
        if (Array.isArray(cached) && cached.length) {
          setOptionLineItems(cached);
        }
      }

      try {
        const res = await axiosInstance.get(`/api/quotes/${id}/options/${optionId}/line-items`);
        const items = Array.isArray(res?.data) ? res.data : [];

        setOptionLineItemsByOptionId((prev) => ({ ...prev, [optionId]: items }));
        if (!opts.prefetch && optionId === selectedOptionIdRef.current) {
          setOptionLineItems(items);
        }

        const t = computeOptionTotals(items);
        setQuoteOptions((prev) =>
          (prev || []).map((o) =>
            o.id === optionId
              ? {
                  ...o,
                  subtotal_amount: t.subtotal,
                  tax_amount: t.tax,
                  total_amount: t.total,
                  total: t.total,
                }
              : o
          )
        );

        return items;
      } catch (e) {
        console.error('Failed to load option line items:', e);
        if (!opts.prefetch && optionId === selectedOptionIdRef.current) {
          const cached = optionLineItemsByOptionIdRef.current?.[optionId];
          if (!Array.isArray(cached) || cached.length === 0) setOptionLineItems([]);
        }
        return [];
      }
    },
    [axiosInstance, quoteId, computeOptionTotals]
  );

  const apiPatchQuoteOption = useCallback(
    async (qid, optionId, payload) => {
      const id = qid || quoteId;
      if (!id || !optionId || !axiosInstance) return null;
      const res = await axiosInstance.patch(`/api/quotes/${id}/options/${optionId}`, payload);
      return res?.data || null;
    },
    [axiosInstance, quoteId]
  );


  const apiCreateOptionLineItem = useCallback(
    async (qid, optionId, payload) => {
      const id = qid || quoteId;
      if (!id || !optionId || !axiosInstance) return null;
      const res = await axiosInstance.post(`/api/quotes/${id}/options/${optionId}/line-items`, payload);
      return res?.data || null;
    },
    [axiosInstance, quoteId]
  );

  const apiPatchOptionLineItem = useCallback(
    async (qid, optionId, lineItemId, payload) => {
      const id = qid || quoteId;
      if (!id || !optionId || !lineItemId || !axiosInstance) return null;
      const res = await axiosInstance.patch(
        `/api/quotes/${id}/options/${optionId}/line-items/${lineItemId}`,
        payload
      );
      return res?.data || null;
    },
    [axiosInstance, quoteId]
  );

  const apiDeleteOptionLineItem = useCallback(
    async (qid, optionId, lineItemId) => {
      const id = qid || quoteId;
      if (!id || !optionId || !lineItemId || !axiosInstance) return false;
      await axiosInstance.delete(`/api/quotes/${id}/options/${optionId}/line-items/${lineItemId}`);
      return true;
    },
    [axiosInstance, quoteId]
  );

  const resetOptionLineItemForm = useCallback(() => {
    setOptLiDescription("");
    setOptLiQty(1);
    setOptLiUnitPrice(0);
    setOptLiVatRate(0.2);
    setOptLiUnitCost("");
    setOptLiError("");
  }, []);

  const applyPbItemToOptionLineItemForm = useCallback((pb) => {
    if (!pb) return;

    const desc = String(pb?.name || pb?.service_name || pb?.description || "").trim();
    const unitPrice = Number(pb?.unit_price ?? pb?.price ?? pb?.sell_price ?? 0) || 0;
    const unitCost = pb?.unit_cost ?? pb?.cost ?? "";
    const taxRate = resolveCatalogVatRate(pb);

    setOptLiDescription(desc || "");
    setOptLiQty(1);
    setOptLiUnitPrice(unitPrice);
    setOptLiVatRate(taxRate);
    setOptLiUnitCost(unitCost === null || unitCost === undefined ? "" : String(unitCost));
    setOptLiError("");
  }, []);

const handleAddOptionLineItem = useCallback(
  async () => {
    if (!multiOptionEnabled) return;
    if (!quoteId) return;
    if (!selectedOptionId) return;

    const description = String(optLiDescription || "").trim();
    const quantity = Math.max(1, parseInt(String(optLiQty || "1"), 10) || 1);
    const unit_price = Number(optLiUnitPrice ?? 0) || 0;

    let tax_rate = Number(optLiVatRate ?? 0);
    if (!Number.isFinite(tax_rate) || tax_rate < 0) tax_rate = 0;
    if (tax_rate > 1.000001) tax_rate = tax_rate / 100.0;

    const unit_cost_raw = String(optLiUnitCost ?? "").trim();
    const unit_cost = unit_cost_raw === "" ? null : Number(unit_cost_raw);

    if (!description) {
      setOptLiError("Description is required.");
      onAlert?.("warning", "Description is required.");
      return;
    }

    setOptLiSaving(true);
    setOptLiError("");

    try {
      const existing = optionLineItemsByOptionIdRef.current?.[selectedOptionId] || [];
      const payload = {
        description,
        quantity,
        unit_price,
        tax_rate,
        sort_order: Array.isArray(existing) ? existing.length : 0,
      };

      if (unit_cost !== null && Number.isFinite(unit_cost)) {
        payload.unit_cost = unit_cost;
      }

      const created = await apiCreateOptionLineItem(quoteId, selectedOptionId, payload);

      if (created && created.id) {
        setOptionLineItems((prev) => {
          const next = [...(Array.isArray(prev) ? prev : []), created];
          setOptionLineItemsByOptionId((m) => ({ ...(m || {}), [selectedOptionId]: next }));

          const t = computeOptionTotals(next);
          setQuoteOptions((opts) =>
            (Array.isArray(opts) ? opts : []).map((o) =>
              o.id === selectedOptionId
                ? {
                    ...o,
                    subtotal_amount: t.subtotal,
                    tax_amount: t.tax,
                    total_amount: t.total,
                    total: t.total,
                  }
                : o
            )
          );

          return next;
        });
      } else {
        await loadOptionLineItems(quoteId, selectedOptionId);
      }

      resetOptionLineItemForm();
      setDirty(true);
      setLastSavedAt(Date.now());
      onAlert?.("success", "Option line item added.");
    } catch (e) {
      const info = getAxiosErrInfo(e);
      setOptLiError(info.msg);
      onAlert?.("warning", `Failed to add line item${info.status ? ` (${info.status})` : ""}: ${info.msg}`);
      console.error("Failed to add option line item:", e);
    } finally {
      setOptLiSaving(false);
    }
  },
  [
    multiOptionEnabled,
    quoteId,
    selectedOptionId,
    optLiDescription,
    optLiQty,
    optLiUnitPrice,
    optLiVatRate,
    optLiUnitCost,
    apiCreateOptionLineItem,
    loadOptionLineItems,
    computeOptionTotals,
    resetOptionLineItemForm,
    onAlert,
  ]
);

  const handleSaveSelectedOptionLabel = useCallback(
    async () => {
      if (!multiOptionEnabled || !quoteId || !selectedOptionId) return;

      const label = String(optionLabelDraft || "").trim();
      if (!label) return;

      try {
        const updated = await apiPatchQuoteOption(quoteId, selectedOptionId, { label });

        if (updated) {
          setQuoteOptions((prev) =>
            (Array.isArray(prev) ? prev : []).map((o) => (o?.id === selectedOptionId ? { ...o, ...updated } : o))
          );
        } else {
          setQuoteOptions((prev) =>
            (Array.isArray(prev) ? prev : []).map((o) => (o?.id === selectedOptionId ? { ...o, label } : o))
          );
        }
      } catch (e) {
        // Don't block UX if backend is temporarily unavailable
        setQuoteOptions((prev) =>
          (Array.isArray(prev) ? prev : []).map((o) => (o?.id === selectedOptionId ? { ...o, label } : o))
        );
        console.error("Failed to update option label", e);
      }
    },
    [apiPatchQuoteOption, multiOptionEnabled, optionLabelDraft, quoteId, selectedOptionId]
  );



  const handleDeleteOptionLineItem = useCallback(
    async (lineItemId) => {
      if (!multiOptionEnabled) return;
      if (!quoteId) return;
      if (!selectedOptionId) return;
      if (!lineItemId) return;

      try {
        await apiDeleteOptionLineItem(quoteId, selectedOptionId, lineItemId);

        setOptionLineItems((prev) => {
          const next = (prev || []).filter((li) => li.id !== lineItemId);
          setOptionLineItemsByOptionId((m) => ({ ...m, [selectedOptionId]: next }));

          const t = computeOptionTotals(next);
          setQuoteOptions((opts) =>
            (opts || []).map((o) =>
              o.id === selectedOptionId
                ? {
                    ...o,
                    subtotal_amount: t.subtotal,
                    tax_amount: t.tax,
                    total_amount: t.total,
                    total: t.total,
                  }
                : o
            )
          );

          return next;
        });

        setDirty(true);
      } catch (e) {
        console.error('Failed to delete option line item:', e);
        alert('Failed to delete line item');
      }
    },
    [
      multiOptionEnabled,
      quoteId,
      selectedOptionId,
      apiDeleteOptionLineItem,
      computeOptionTotals,
    ]
  );

  const handlePatchOptionLineItemField = useCallback(
    async (lineItemId, patch) => {
      if (!multiOptionEnabled) return;
      if (!quoteId) return;
      if (!selectedOptionId) return;
      if (!lineItemId) return;

      const allowed = new Set(["description", "quantity", "unit_price", "tax_rate", "unit_cost"]);
      const raw = patch && typeof patch === "object" ? patch : {};
      const current = (Array.isArray(optionLineItems) ? optionLineItems : []).find((x) => x?.id === lineItemId) || null;

      const payload = {};
      Object.keys(raw).forEach((k) => {
        if (!allowed.has(k)) return;

        let v = raw[k];

        if (k === "description") {
          if (v === undefined) return;
          if (v === null) {
            payload[k] = null;
            return;
          }
          const s = String(v);
          // Treat empty string as clearing description
          payload[k] = s.trim() === "" ? null : s;
          return;
        }

        if (k === "unit_cost") {
          if (v === undefined) return;
          if (v === null) {
            payload[k] = null;
            return;
          }
          const s = String(v).trim();
          payload[k] = s === "" ? null : s;
          return;
        }

        // numeric fields
        if (v === "" || v === undefined || v === null) return;

        if (k === "quantity") {
          const nInt = parseInt(String(v), 10);
          if (Number.isNaN(nInt)) return;
          payload[k] = nInt;
          return;
        }

        const num = Number(v);
        if (Number.isNaN(num)) return;
        payload[k] = num;
      });

      if (Object.keys(payload).length === 0) return;

      try {
        const updated = await apiPatchOptionLineItem(quoteId, selectedOptionId, lineItemId, payload);

        setOptionLineItems((prev) => {
          const nextItems = (Array.isArray(prev) ? prev : []).map((li) => {
            if (li?.id !== lineItemId) return li;
            if (updated && typeof updated === "object") return { ...li, ...updated };
            return { ...li, ...payload };
          });

          setOptionLineItemsByOptionId((m) => ({ ...m, [selectedOptionId]: nextItems }));

          const t = computeOptionTotals(nextItems);
          setQuoteOptions((opts) =>
            (Array.isArray(opts) ? opts : []).map((o) =>
              o?.id === selectedOptionId
                ? {
                    ...o,
                    subtotal_amount: t.subtotal,
                    tax_amount: t.tax,
                    total_amount: t.total,
                    total: t.total,
                  }
                : o
            )
          );

          return nextItems;
        });

        setDirty(true);
      } catch (e) {
        console.error("Failed to update option line item:", e);
        alert("Failed to update line item");
      }
    },
    [
      multiOptionEnabled,
      quoteId,
      selectedOptionId,
      optionLineItems,
      apiPatchOptionLineItem,
      computeOptionTotals,
    ]
  );

  useEffect(() => {
    if (!quoteId || !multiOptionEnabled) return;
    (async () => {
      // Ensure options exist, then load.
      await ensureDefaultOptionsABC(quoteId);
    })();
  }, [quoteId, multiOptionEnabled, ensureDefaultOptionsABC]);

  useEffect(() => {
    if (!multiOptionEnabled) return;
    if (!quoteId) return;
    if (!quoteOptions || quoteOptions.length === 0) return;

    for (const opt of quoteOptions) {
      if (!opt?.id) continue;
      if (optionLineItemsByOptionIdRef.current?.[opt.id]) continue;
      loadOptionLineItems(quoteId, opt.id, { prefetch: true });
    }
  }, [multiOptionEnabled, quoteId, quoteOptions, loadOptionLineItems]);


  useEffect(() => {
    if (!quoteId || !multiOptionEnabled || !selectedOptionId) {
      setOptionLineItems([]);
      return;
    }
    (async () => {
      await loadOptionLineItems(quoteId, selectedOptionId);
    })();
  }, [quoteId, multiOptionEnabled, selectedOptionId, loadOptionLineItems]);

  // Keep option label draft in sync with selected option
  useEffect(() => {
    if (!multiOptionEnabled) return;
    if (!selectedOptionId) {
      setOptionLabelDraft("");
      return;
    }
    const opt = (Array.isArray(quoteOptions) ? quoteOptions : []).find((o) => o?.id === selectedOptionId);
    const lbl = opt?.label || opt?.option_key || "";
    setOptionLabelDraft(String(lbl || ""));
  }, [multiOptionEnabled, selectedOptionId, quoteOptions]);




  const loadPricebookItems = useCallback(async (pbid) => {
    const id = toIntOrNull(pbid) ?? (norm(pbid) ? pbid : null);
    if (!id) {
      setPbItems([]);
      setPbItemsErr("");
      return;
    }

    setPbItemsErr("");
    setPbItemsBusy(true);
    try {
      // canonical: /api/quote-catalog/pricebooks/{pricebook_id}/items
      const resp = await axiosInstance.get(`${PRICEBOOKS_BASE}/${id}/items`, { params: { include_inactive: false } });
      const rows = toArray(resp?.data);
      const arr = Array.isArray(rows) ? rows : [];
      arr.sort((a, b) => String(pbItemLabel(a)).localeCompare(String(pbItemLabel(b))));
      setPbItems(arr);
    } catch (e) {
      const info = getAxiosErrInfo(e);
      setPbItems([]);
      setPbItemsErr(`Failed to load pricebook items${info.status ? ` (${info.status})` : ""}: ${info.msg}`);
    } finally {
      setPbItemsBusy(false);
    }
  }, []);

  useEffect(() => {
    if (!open) return;
    loadEligiblePricebooks();
  }, [open, loadEligiblePricebooks]);

  useEffect(() => {
    if (!open) return;
    if (!selectedPricebookId) {
      setPbItems([]);
      setPbItemsErr("");
      return;
    }
    loadPricebookItems(selectedPricebookId);
  }, [open, selectedPricebookId, loadPricebookItems]);

  // --- load terms lists -----------------------------------------------------
  const loadTermsLists = useCallback(async (opts = {}) => {
    const include_inactive = !!opts.include_inactive;

    setQuoteTermsErr("");
    setServiceTermsErr("");

    setQuoteTermsBusy(true);
    try {
      const rows = await fetchTermsByKey(QUOTE_TERMS_KEY, include_inactive);
      const sorted = [...(Array.isArray(rows) ? rows : [])].sort((a, b) => Number(b?.version || 0) - Number(a?.version || 0));
      setQuoteTermsDocs(sorted);
    } catch (e) {
      const info = getAxiosErrInfo(e);
      setQuoteTermsDocs([]);
      setQuoteTermsErr(`Failed to load Terms documents (${QUOTE_TERMS_KEY})${info.status ? ` (${info.status})` : ""}: ${info.msg}`);
    } finally {
      setQuoteTermsBusy(false);
    }

    setServiceTermsBusy(true);
    try {
      const rows = await fetchTermsByKey(SERVICE_TERMS_KEY, include_inactive);
      const sorted = [...(Array.isArray(rows) ? rows : [])].sort((a, b) => Number(b?.version || 0) - Number(a?.version || 0));
      setServiceTermsDocs(sorted);
    } catch (e) {
      const info = getAxiosErrInfo(e);
      setServiceTermsDocs([]);
      setServiceTermsErr(
        `Failed to load Terms documents (${SERVICE_TERMS_KEY})${info.status ? ` (${info.status})` : ""}: ${info.msg}`
      );
    } finally {
      setServiceTermsBusy(false);
    }
  }, []);

  useEffect(() => {
    if (!open) return;
    loadTermsLists({ include_inactive: false });
  }, [open, loadTermsLists]);

  // ---------- EDIT/CREATE SEED ----------
  useEffect(() => {
    if (!open) return;

    let cancelled = false;

    const cleanupPdf = () => {
      setPdfErr("");
      setPdfTriedPath("");
      setPdfTriedStatus(null);
      if (pdfUrlRef.current) {
        try {
          URL.revokeObjectURL(pdfUrlRef.current);
        } catch {}
      }
      setPdfUrl("");
      setPreviewNonce(0);
    };

    const resetCommon = () => {
      setSaveErr("");
      setContacts([]);
      setContactsBusy(false);

      setAllOpps([]);
      setOppsBusy(false);
      setRawOppsCount(0);
      setLastOppsFetchErr("");

      setQuoteTermsDocumentId(null);
      setServiceTermsDocumentId(null);

      setLiMeta(null);
      setLiErr("");
      setLiBusy(false);
      setLiCommitBusyId(null);
      setLiDraftAdding(false);

      setManualDesc("");
      setManualQty("1");
      setManualUnitPrice("");
      setManualVatPct("20");

      setQuoteTitle("");

      // (3) status reset
      setQuoteStatus("");

      // ✅ multi-option UI reset
      setMultiOptionEnabled(false);

      // pricebook selection
      setSelectedPricebookId("");
      setPbItems([]);
      setPbItemsErr("");

      cleanupPdf();

      setDirty(false);
      setSaving(false);
      setPreviewLoading(false);
      setLastSavedAt(null);
      setLastPreviewAt(null);
    };

    const runCreateSeed = () => {
      resetCommon();
      setQuoteId(null);

      setCustomerId(norm(defaultCustomerId) ? String(defaultCustomerId) : "");
      setOpportunityId(
        norm(contextOpportunityId)
          ? String(contextOpportunityId)
          : norm(defaultOpportunityId)
          ? String(defaultOpportunityId)
          : ""
      );
      setCurrency("GBP");

      setContactId("");
      setToEmail("");

      setLineItems([]);
      setBodyHtml(defaultBodyHtml || "<p></p>");
      setSpecificNotes("");

      // (3) create-mode assumed draft until created response says otherwise
      setQuoteStatus("draft");
    };

    const runEditLoad = async (qid) => {
      resetCommon();
      setQuoteId(qid);

      setCustomerId("");
      setOpportunityId("");
      setCurrency("GBP");
      setContactId("");
      setToEmail("");
      setLineItems([]);
      setBodyHtml("<p></p>");
      setSpecificNotes("");

      try {
        const qRes = await axiosInstance.get(`/api/quotes/${qid}`);
        const q = qRes?.data || {};

        if (cancelled) return;

        const cid = q?.customer_id ?? q?.customerId ?? "";
        const oid = q?.opportunity_id ?? q?.opportunityId ?? "";
        const cur = q?.currency ?? "GBP";
        const body = (q?.body_html ?? q?.bodyHtml ?? defaultBodyHtml) || "<p></p>";
        const contact = q?.contact_id ?? q?.contactId ?? "";
        const email = q?.contact_email ?? q?.contactEmail ?? "";

        const ttl = q?.title ?? q?.quote_title ?? q?.quoteTitle ?? "";

        const qTermsId = q?.quote_terms_document_id ?? q?.quoteTermsDocumentId ?? null;
        const sTermsId = q?.service_terms_document_id ?? q?.serviceTermsDocumentId ?? null;

        // ✅ multi-option flag (defensive): support both snake_case and camelCase
        const mo = q?.multi_option_enabled ?? q?.multiOptionEnabled ?? null;
        if (typeof mo === "boolean") setMultiOptionEnabled(mo);

          const sub = q?.show_unit_breakdown;
          if (typeof sub === "boolean") setShowUnitBreakdown(sub);
          else setShowUnitBreakdown(false);

        // (3) status from quote payload (defensive)
        const st = q?.status ?? q?.quote_status ?? q?.current_status ?? null;
        if (st != null && String(st).trim()) setQuoteStatus(String(st));

        setCustomerId(cid != null ? String(cid) : "");
        setOpportunityId(oid != null ? String(oid) : "");
        setCurrency(String(cur || "GBP").toUpperCase());

        setQuoteTitle(String(ttl || ""));

        setContactId(contact != null ? String(contact) : "");
        // HARDEN: keep backend email as a fallback; DO NOT allow later effects to wipe it.
        setToEmail(String(email || ""));

        setQuoteTermsDocumentId(qTermsId != null && String(qTermsId).trim() ? qTermsId : null);
        setServiceTermsDocumentId(sTermsId != null && String(sTermsId).trim() ? sTermsId : null);

        setBodyHtml(String(body || "<p></p>"));
        setSpecificNotes(String(q?.specific_notes ?? q?.specificNotes ?? q?.specific_note ?? ""));

        await loadLineItems(qid);

        setDirty(false);
        setLastSavedAt(Date.now());
      } catch (e) {
        const info = getAxiosErrInfo(e);
        onAlert?.("critical", `Failed to load quote #${qid}${info.status ? ` (${info.status})` : ""}: ${info.msg}`);
      }
    };

    const effectiveQid = toIntOrNull(contextQuoteId) ?? toIntOrNull(quoteIdProp);

    if (effectiveQid) runEditLoad(effectiveQid);
    else runCreateSeed();

    return () => {
      cancelled = true;
    };
  }, [
    open,
    contextQuoteId,
    quoteIdProp,
    defaultCustomerId,
    defaultOpportunityId,
    contextOpportunityId,
    defaultBodyHtml,
    onAlert,
    loadLineItems,
  ]);

  // Load contacts when customer changes
  useEffect(() => {
    if (!open) return;

    const cid = toIntOrNull(customerId);
    if (!cid) {
      setContacts([]);
      if (!quoteId) {
        setContactId("");
        setToEmail("");
      }
      return;
    }

    let cancelled = false;
    setContactsBusy(true);

    const run = async () => {
      try {
        let rows = [];

        if (typeof onLoadContacts === "function") {
          const res = await onLoadContacts(cid);
          rows = toArray(res);
        } else {
          const resp = await axiosInstance.get("/api/contacts", { params: { limit: 500, offset: 0 } });
          rows = toArray(resp?.data);
        }

        if (cancelled) return;

        const filtered = rows.filter((c) => Number(c?.customer_id ?? c?.customerId ?? c?.client_id ?? c?.account_id ?? 0) === Number(cid));
        setContacts(filtered);

        const curId = toIntOrNull(contactId);
        if (curId) {
          const chosen = filtered.find((x) => Number(x?.id) === Number(curId));
          if (chosen) {
            const derived = deriveEmailFromContact(chosen);
            // HARDEN: only fill if we can derive something real; otherwise preserve existing (quote.contact_email fallback)
            if (norm(derived) && !norm(toEmail)) setToEmail(derived);
            if (norm(derived) && norm(toEmail) !== norm(derived)) setToEmail(derived);
            return;
          }
        }

        if (!quoteId && filtered.length === 1) {
          const only = filtered[0];
          setContactId(String(only?.id ?? ""));
          setToEmail(deriveEmailFromContact(only));
        } else if (!quoteId) {
          setContactId("");
          setToEmail("");
        }
      } catch (e) {
        if (cancelled) return;
        setContacts([]);
        if (!quoteId) {
          setContactId("");
          setToEmail("");
        }
        onAlert?.("warning", e?.response?.data?.detail || e?.message || "Failed to load contacts for customer.");
      } finally {
        if (cancelled) return;
        setContactsBusy(false);
      }
    };

    run();
    return () => {
      cancelled = true;
    };
  }, [open, customerId, onLoadContacts, onAlert, contactId, toEmail, quoteId]);

  // HARDEN: do not wipe toEmail while contacts are not yet available (prevents blank "To")
  useEffect(() => {
    if (!open) return;

    const id = toIntOrNull(contactId);
    if (!id) {
      setToEmail("");
      return;
    }

    const c = (Array.isArray(contacts) ? contacts : []).find((x) => Number(x?.id) === Number(id));
    if (!c) {
      // contacts not loaded yet (or contact not in list) — keep whatever we already have (e.g. quote.contact_email)
      return;
    }

    const derived = deriveEmailFromContact(c);
    if (!norm(derived)) return;

    // Only set if it's actually different
    if (norm(toEmail) !== norm(derived)) setToEmail(derived);
  }, [open, contactId, contacts, toEmail]);

  const contactOptions = useMemo(() => {
    const arr = Array.isArray(contacts) ? [...contacts] : [];
    arr.sort((a, b) => String(contactLabel(a)).localeCompare(String(contactLabel(b))));
    return arr;
  }, [contacts, contactLabel]);

  // Load opportunities when customer changes
  useEffect(() => {
    if (!open) return;

    const cid = toIntOrNull(customerId);
    if (!cid) {
      setAllOpps([]);
      setRawOppsCount(0);
      setLastOppsFetchErr("");
      return;
    }

    let cancelled = false;
    setOppsBusy(true);
    setLastOppsFetchErr("");

    const run = async () => {
      try {
        const limit = 500;
        const maxPagesGuard = 50;

        let combined = [];
        let offset = 0;
        let pages = 0;

        const base = "/api/opportunities";

        // eslint-disable-next-line no-constant-condition
        while (true) {
          pages += 1;
          if (pages > maxPagesGuard) break;

          const resp = await axiosInstance.get(base, { params: { limit, offset } });
          const rows = toArray(resp?.data);
          const arr = Array.isArray(rows) ? rows : [];

          combined = combined.concat(arr);

          if (arr.length < limit) break;
          offset += limit;
        }

        if (cancelled) return;

        setAllOpps(Array.isArray(combined) ? combined : []);
        setRawOppsCount(Array.isArray(combined) ? combined.length : 0);
        setLastOppsFetchErr("");
      } catch (e) {
        if (cancelled) return;
        setAllOpps([]);
        setRawOppsCount(0);
        const msg = e?.response?.data?.detail || e?.message || "Failed to load opportunities.";
        setLastOppsFetchErr(String(msg));
        onAlert?.("warning", msg);
      } finally {
        if (cancelled) return;
        setOppsBusy(false);
      }
    };

    run();
    return () => {
      cancelled = true;
    };
  }, [open, customerId, onAlert]);

  const openOppsForSelection = useMemo(() => {
    const cid = toIntOrNull(customerId);
    if (!cid) return [];

    const contact = toIntOrNull(contactId);

    return (Array.isArray(allOpps) ? allOpps : [])
      .filter((o) => Number(getCustomerIdFromOpp(o)) === Number(cid))
      .filter((o) => isOpenOpportunity(o))
      .filter((o) => {
        if (!contact) return true;
        const pcid = getPrimaryContactIdFromOpp(o);
        if (!pcid) return true;
        return Number(pcid) === Number(contact);
      })
      .sort((a, b) => Number(b?.id || 0) - Number(a?.id || 0));
  }, [allOpps, customerId, contactId]);

  const openOppsSummary = useMemo(() => {
    const count = openOppsForSelection.length;
    const total = openOppsForSelection.reduce((acc, o) => acc + (Number.isFinite(getOppAmount(o)) ? getOppAmount(o) : 0), 0);
    return { count, total };
  }, [openOppsForSelection]);

  const oppOptions = useMemo(() => {
    return openOppsForSelection.map((o) => ({
      id: String(o?.id ?? ""),
      label: oppLabel(o, currency),
      amount: getOppAmount(o),
    }));
  }, [openOppsForSelection, currency]);

  const matchedOppsCount = oppOptions.length;

  const totals = useMemo(() => {
    const items = Array.isArray(lineItems) ? lineItems : [];
    const subtotal = items.reduce((acc, li) => acc + calcLineSubtotal(li), 0);
    const vatTotal = items.reduce((acc, li) => acc + calcLineVat(li), 0);
    const grandTotal = items.reduce((acc, li) => acc + calcLineTotal(li), 0);
    return { subtotal, vatTotal, grandTotal };
  }, [lineItems]);

  const addPricebookItem = async (it) => {
    const desc = norm(it?.description) || norm(it?.name) || "";
    const unit_price = Number(it?.unit_price ?? it?.unitPrice ?? it?.price ?? 0) || 0;

    // ✅ FIX: pricebook items missing tax should default to 20%
    const vatRate = resolveCatalogVatRate(it);
    const qty = 1;

    if (!desc) {
      onAlert?.("warning", "Cannot add item: missing description.");
      return;
    }

    // pre-create draft behaviour
    if (!quoteId) {
      setLineItems((prev) => [
        ...(Array.isArray(prev) ? prev : []),
        { description: desc, name: desc, qty, quantity: qty, unit_price, tax_rate: vatRate, service_id: it?.service_id ?? null },
      ]);
      return;
    }

    if (!liEditable) {
      onAlert?.("warning", quoteLocked ? "Quote is locked by status." : "Line items are locked by quote status.");
      return;
    }

    setLiErr("");
    setLiDraftAdding(true);
    try {
      await apiCreateLineItem(quoteId, {
        description: desc,
        quantity: qty,
        unit_price,
        tax_rate: vatRate,
        sort_order: (Array.isArray(lineItems) ? lineItems.length : 0) || 0,
        service_id: it?.service_id ?? it?.service?.id ?? null,
      });
      await loadLineItems(quoteId);
      setLastSavedAt(Date.now());
      onAlert?.("success", "Line item added.");
    } catch (e) {
      const info = getAxiosErrInfo(e);
      setLiErr(info.msg);
      onAlert?.("warning", info.msg);
    } finally {
      setLiDraftAdding(false);
    }
  };

  const updateLineItemLocal = (idx, patch) => {
    setLineItems((prev) => {
      const arr = Array.isArray(prev) ? [...prev] : [];
      if (!arr[idx]) return arr;
      arr[idx] = { ...arr[idx], ...patch };
      return arr;
    });
  };

  const removeLineItemLocal = (idx) => {
    setLineItems((prev) => {
      const arr = Array.isArray(prev) ? [...prev] : [];
      arr.splice(idx, 1);
      return arr;
    });
  };

  const commitLineItemPatch = async (liId, updates) => {
    if (!quoteId) return;
    if (!liEditable) {
      onAlert?.("warning", quoteLocked ? "Quote is locked by status." : "Line items are locked by quote status.");
      return;
    }

    const id = toIntOrNull(liId);
    if (!id) {
      // (2) hard guard: never allow PATCH to run with missing id (avoids collection/redirect gotchas)
      onAlert?.("warning", "Cannot update line item: missing line item id.");
      return;
    }

    setLiErr("");
    setLiCommitBusyId(id);
    try {
      await apiPatchLineItem(quoteId, id, updates);
      await loadLineItems(quoteId);
      setLastSavedAt(Date.now());
    } catch (e) {
      const info = getAxiosErrInfo(e);
      setLiErr(info.msg);
      onAlert?.("warning", info.msg);
    } finally {
      setLiCommitBusyId(null);
    }
  };

  const deleteLineItem = async (liId) => {
    if (!quoteId) return;
    if (!liEditable) {
      onAlert?.("warning", quoteLocked ? "Quote is locked by status." : "Line items are locked by quote status.");
      return;
    }

    const id = toIntOrNull(liId);
    if (!id) {
      // (2) hard guard: never allow DELETE to run with missing id (avoids collection/redirect gotchas)
      onAlert?.("warning", "Cannot delete line item: missing line item id.");
      return;
    }

    setLiErr("");
    setLiCommitBusyId(id);
    try {
      await apiDeleteLineItem(quoteId, id);
      await loadLineItems(quoteId);
      setLastSavedAt(Date.now());
      onAlert?.("success", "Line item deleted.");
    } catch (e) {
      const info = getAxiosErrInfo(e);
      setLiErr(info.msg);
      onAlert?.("warning", info.msg);
    } finally {
      setLiCommitBusyId(null);
    }
  };

  const handleAddManualItem = async () => {
    const desc = norm(manualDesc);
    const qty = Math.max(0, Math.floor(nnum(manualQty, 0)));
    const unit = nnum(manualUnitPrice, 0);
    const vatRate = pctToRate(manualVatPct);

    if (!desc) {
      onAlert?.("warning", "Description is required.");
      return;
    }
    if (qty <= 0) {
      onAlert?.("warning", "Qty must be > 0.");
      return;
    }

    // pre-create draft behaviour
    if (!quoteId) {
      setLineItems((prev) => [
        ...(Array.isArray(prev) ? prev : []),
        { description: desc, name: desc, qty, quantity: qty, unit_price: unit, tax_rate: vatRate },
      ]);
      setManualDesc("");
      setManualQty("1");
      setManualUnitPrice("");
      setManualVatPct(String(Math.round(rateToPct(vatRate))));
      return;
    }

    if (!liEditable) {
      onAlert?.("warning", quoteLocked ? "Quote is locked by status." : "Line items are locked by quote status.");
      return;
    }

    setLiErr("");
    setLiDraftAdding(true);
    try {
      await apiCreateLineItem(quoteId, {
        description: desc,
        quantity: qty,
        unit_price: unit,
        tax_rate: vatRate,
        sort_order: (Array.isArray(lineItems) ? lineItems.length : 0) || 0,
      });
      await loadLineItems(quoteId);
      setLastSavedAt(Date.now());

      setManualDesc("");
      setManualQty("1");
      setManualUnitPrice("");
      setManualVatPct(String(Math.round(rateToPct(vatRate))));
      onAlert?.("success", "Line item added.");
    } catch (e) {
      const info = getAxiosErrInfo(e);
      setLiErr(info.msg);
      onAlert?.("warning", info.msg);
    } finally {
      setLiDraftAdding(false);
    }
  };

  const buildQuoteCreatePayload = () => {
    const cid = toIntOrNull(customerId);
    if (!cid) return null;

    const oidRaw = norm(effectiveOpportunityId);
    const oid = oidRaw ? toIntOrNull(oidRaw) : null;

    const payload = {
      customer_id: cid,
      opportunity_id: oid,
      currency: String(currency || "GBP").toUpperCase(),

      // ADD: title
      title: norm(quoteTitle) || null,

      contact_id: toIntOrNull(contactId),
      contact_email: norm(toEmail) || null,
      body_html: String(bodyHtml || "").trim() || "<p></p>",
      public_notes: norm(htmlToPlainText(bodyHtml)) || null,
      specific_notes: norm(specificNotes) || null,

      quote_terms_document_id: quoteTermsDocumentId ?? null,
      service_terms_document_id: serviceTermsDocumentId ?? null,
      multi_option_enabled: !!multiOptionEnabled,
    };

    if (DEBUG) {
      // eslint-disable-next-line no-console
      console.log("CREATE TERMS PAYLOAD", {
        quote_terms_document_id: payload.quote_terms_document_id,
        service_terms_document_id: payload.service_terms_document_id,
        types: [typeof payload.quote_terms_document_id, typeof payload.service_terms_document_id],
        modes: [quoteTermsIdMode.mode, serviceTermsIdMode.mode],
      });
    }

    return payload;
  };

  const buildQuotePatchPayload = () => {
    const cid = toIntOrNull(customerId);
    if (!cid) return null;

    const oidRaw = norm(effectiveOpportunityId);
    const oid = oidRaw ? toIntOrNull(oidRaw) : null;

    const payload = {
      customer_id: cid,
      opportunity_id: oid,
      currency: String(currency || "GBP").toUpperCase(),

      // ADD: title
      title: norm(quoteTitle) || null,

      contact_id: toIntOrNull(contactId),
      contact_email: norm(toEmail) || null,
      body_html: String(bodyHtml || "").trim() || "<p></p>",
      public_notes: norm(htmlToPlainText(bodyHtml)) || null,
      specific_notes: norm(specificNotes) || null,

      quote_terms_document_id: quoteTermsDocumentId ?? null,
      service_terms_document_id: serviceTermsDocumentId ?? null,
      multi_option_enabled: !!multiOptionEnabled,
    show_unit_breakdown: !!showUnitBreakdown,
    };

    if (DEBUG) {
      // eslint-disable-next-line no-console
      console.log("PATCH TERMS PAYLOAD", {
        quote_terms_document_id: payload.quote_terms_document_id,
        service_terms_document_id: payload.service_terms_document_id,
        types: [typeof payload.quote_terms_document_id, typeof payload.service_terms_document_id],
        modes: [quoteTermsIdMode.mode, serviceTermsIdMode.mode],
      });
    }

    return payload;
  };

  const applyQuoteFromResponseIfPresent = (q) => {
    if (!q || typeof q !== "object") return;

    const cid = q?.customer_id ?? q?.customerId ?? null;
    const oid = q?.opportunity_id ?? q?.opportunityId ?? null;
    const cur = q?.currency ?? null;
    const contact = q?.contact_id ?? q?.contactId ?? null;
    const email = q?.contact_email ?? q?.contactEmail ?? null;
    const body = q?.body_html ?? q?.bodyHtml ?? null;

    // ADD: title
    const ttl = q?.title ?? q?.quote_title ?? q?.quoteTitle ?? null;

    const qTermsId = q?.quote_terms_document_id ?? q?.quoteTermsDocumentId ?? null;
    const sTermsId = q?.service_terms_document_id ?? q?.serviceTermsDocumentId ?? null;

    // ✅ multi-option flag (defensive): support both snake_case and camelCase
    const mo = q?.multi_option_enabled ?? q?.multiOptionEnabled ?? null;
    if (typeof mo === "boolean") setMultiOptionEnabled(mo);


    // PDF display preference: show/hide unit breakdown (persisted on quotes.show_unit_breakdown).
    const sub = q?.show_unit_breakdown;
    setShowUnitBreakdown(typeof sub === "boolean" ? sub : false);
    // (3) status sync if present
    const st = q?.status ?? q?.quote_status ?? q?.current_status ?? null;
    if (st != null && String(st).trim()) setQuoteStatus(String(st));

    if (cid != null) setCustomerId(String(cid || ""));
    if (oid != null) setOpportunityId(String(oid || ""));
    if (cur != null) setCurrency(String(cur || "GBP").toUpperCase());

    if (ttl != null) setQuoteTitle(String(ttl || ""));

    if (contact != null) setContactId(String(contact || ""));
    if (email != null && String(email).trim()) setToEmail(String(email || ""));

    if (qTermsId != null && String(qTermsId).trim()) setQuoteTermsDocumentId(qTermsId);
    else setQuoteTermsDocumentId(null);

    if (sTermsId != null && String(sTermsId).trim()) setServiceTermsDocumentId(sTermsId);
    else setServiceTermsDocumentId(null);

    if (body != null) setBodyHtml(String(body || "<p></p>"));
  };

  const refreshPreview = async (opts = {}) => {
    const force = !!opts.force;

    setPdfErr("");
    setPdfTriedPath("");
    setPdfTriedStatus(null);

    if (!quoteId) {
      setPdfErr("Create the quote first to generate a PDF preview.");
      onAlert?.("info", "Create the quote first to generate a PDF preview.");
      return;
    }
    if (!force && dirty) {
      setPdfErr("Preview is out of date. Save changes first, then refresh preview.");
      onAlert?.("warning", "Preview is out of date. Save changes first, then refresh preview.");
      return;
    }

    setPreviewLoading(true);

    try {
      setPreviewNonce((n) => n + 1);

      if (pdfUrlRef.current) {
        try {
          URL.revokeObjectURL(pdfUrlRef.current);
        } catch {}
      }
      setPdfUrl("");

      if (typeof onPreviewPdf === "function") {
        const result = await onPreviewPdf({ quote_id: quoteId, nonce: Date.now() });
        const blob = normalizePdfResultToBlob(result);
        if (!blob) throw new Error("PDF preview did not return a PDF Blob.");
        const pdfBlob = blob.type === "application/pdf" ? blob : new Blob([blob], { type: "application/pdf" });
        const url = URL.createObjectURL(pdfBlob);
        setPdfUrl(url);
        setLastPreviewAt(Date.now());
        setPdfTriedStatus(200);
        return;
      }

      const nonce = Date.now();
      const path = `/api/quotes/${quoteId}/pdf/preview?nonce=${nonce}`;
      setPdfTriedPath(path);

      const resp = await axiosInstance.get(path, { responseType: "blob", headers: { Accept: "application/pdf" } });
      const blob = normalizePdfResultToBlob(resp);
      if (!blob) throw new Error("PDF preview did not return a PDF Blob.");

      const pdfBlob = blob.type === "application/pdf" ? blob : new Blob([blob], { type: "application/pdf" });
      const url = URL.createObjectURL(pdfBlob);
      setPdfUrl(url);
      setLastPreviewAt(Date.now());
      setPdfTriedStatus(200);
    } catch (e) {
      const info = getAxiosErrInfo(e);
      const status = info.status ? ` (${info.status})` : "";
      const msg = `Failed to generate PDF preview${status}: ${info.msg}`;
      setPdfErr(msg);
      setPdfTriedStatus(info.status);
      onAlert?.("warning", msg);
    } finally {
      setPreviewLoading(false);
    }
  };

  const handleCreateDraft = async () => {
    setSaveErr("");

    const cid = toIntOrNull(customerId);
    if (!cid) {
      setSaveErr("Customer is required.");
      onAlert?.("warning", "Customer is required.");
      return null;
    }

    if (typeof onCreate !== "function") {
      setSaveErr("Create handler is missing (onCreate).");
      onAlert?.("critical", "Create handler is missing (onCreate).");
      return null;
    }

    const payload = buildQuoteCreatePayload();
    if (!payload) {
      setSaveErr("Invalid quote payload.");
      onAlert?.("critical", "Invalid quote payload.");
      return null;
    }

    setSaving(true);
    try {
      const created = await onCreate(payload);
      const newId = Number(created?.id);
      if (!Number.isFinite(newId) || newId <= 0) throw new Error("Quote created but no id returned from backend.");

      setQuoteId(newId);
      setDirty(false);
      setLastSavedAt(Date.now());

      // Sync title/status etc if backend returns it (source of truth)
      applyQuoteFromResponseIfPresent(created);

      onAlert?.("success", `Quote created (#${newId}).`);

      const draft = Array.isArray(lineItems) ? [...lineItems] : [];
      if (draft.length) {
        try {
          // Persist pre-create draft line items immediately against the newly
          // created quote. Do not gate this on liMeta state: loadLineItems()
          // updates React state asynchronously, so reading liMeta immediately
          // afterwards can observe the previous/null value and incorrectly
          // skip every line-item POST. Backend governance remains authoritative
          // and will reject creation if the new quote is unexpectedly locked.
          for (let i = 0; i < draft.length; i += 1) {
            const d = draft[i];
            const desc = lineDesc(d);
            if (!desc) continue;
            // eslint-disable-next-line no-await-in-loop
            await apiCreateLineItem(newId, {
              description: desc,
              quantity: lineQty(d) || 1,
              unit_price: lineUnitPrice(d) || 0,
              tax_rate: lineTaxRate(d),
              sort_order: i,
            });
          }
          await loadLineItems(newId);
          setLastSavedAt(Date.now());
        } catch (e) {
          const info = getAxiosErrInfo(e);
          onAlert?.(
            "warning",
            `Quote created, but failed to persist draft line items${info.status ? ` (${info.status})` : ""}: ${info.msg}`
          );
        }
      } else {
        await loadLineItems(newId);
      }

      setTimeout(() => refreshPreview({ force: true }), 0);
      return created;
    } catch (e) {
      const info = getAxiosErrInfo(e);
      const msg = `Failed to create quote${info.status ? ` (${info.status})` : ""}: ${info.msg}`;
      setSaveErr(msg);
      onAlert?.("critical", msg);
      return null;
    } finally {
      setSaving(false);
    }
  };

  const handleSaveChanges = async () => {
    setSaveErr("");

    if (!quoteId) {
      setSaveErr("Create the quote first.");
      onAlert?.("info", "Create the quote first.");
      return false;
    }

    // (3) hard governance guard: prevent saving edits when locked
    if (quoteLocked) {
      onAlert?.("warning", "Quote is locked by status. Editing is disabled.");
      return false;
    }

    const payload = buildQuotePatchPayload();
    if (!payload) {
      setSaveErr("Invalid quote payload.");
      onAlert?.("critical", "Invalid quote payload.");
      return false;
    }

    const captionSlotsToSave = [...imageCaptionDirtySlots];

    setSaving(true);
    try {
      if (dirty) {
        const resp = await axiosInstance.patch(`/api/quotes/${quoteId}`, payload);
        applyQuoteFromResponseIfPresent(resp?.data);
      }

      for (const slot of captionSlotsToSave) {
        // The current quote-image endpoint requires a file. For caption-only
        // edits we re-post the already stored image bytes with the new caption.
        // This preserves the existing API contract while allowing "" to clear it.
        // eslint-disable-next-line no-await-in-loop
        await saveExistingQuoteImageCaption(slot);
      }

      setDirty(false);
      setLastSavedAt(Date.now());
      onAlert?.("success", "Saved.");
      return true;
    } catch (e) {
      const info = getAxiosErrInfo(e);
      const msg = `Save failed${info.status ? ` (${info.status})` : ""}: ${info.msg}`;
      setSaveErr(msg);
      onAlert?.("critical", msg);
      return false;
    } finally {
      setSaving(false);
    }
  };

  const handleSaveAndRefresh = async () => {
    // Save quote edits and caption-only image edits before refreshing preview.
    if (dirty || imageCaptionDirty) {
      const ok = await handleSaveChanges();
      if (ok) setTimeout(() => refreshPreview({ force: true }), 0);
      return;
    }
    if (previewStale) {
      setTimeout(() => refreshPreview({ force: true }), 0);
    }
  };

  useEffect(() => {
    if (!open) return;
    const onKeyDown = (e) => {
      if (e.key === "Escape") {
        e.stopPropagation();
        onClose?.();
      }
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [open, onClose]);

  useEffect(() => {
    return () => {
      if (pdfUrlRef.current) {
        try {
          URL.revokeObjectURL(pdfUrlRef.current);
        } catch {}
      }
    };
  }, []);

  if (!open) return null;

  const title = quoteId ? `Edit Quote #${quoteId}` : "Create Quote";

  // button styling (standard palettes)
  const btnGeneral = "bg-btnGeneralNormal hover:bg-btnGeneralHover active:bg-btnGeneralClick text-white";
  const btnAction = "bg-btnActionNormal hover:bg-btnActionHover active:bg-btnActionClick text-white";

  // NextStepCard gating (MUST NOT change behaviour)
  const nsCanCreateDraft = !quoteId && !saving;
  const nsCanSaveChanges = !!quoteId && (dirty || imageCaptionDirty) && !quoteLocked && !saving;
  const nsCanSaveAndRefresh = !!quoteId && !!previewStale && !quoteLocked && !saving;
  const nsCanRefreshPreview = !!quoteId && !dirty && !imageCaptionDirty && !saving && !previewLoading;

  const switchId = "quote-multi-option-switch";

  return createPortal(
    <div className="fixed inset-0 z-[150500] flex items-center justify-center bg-black/40 p-0 sm:p-6" onMouseDown={onClose}>
      <div
        className="relative flex h-[100dvh] max-h-[100dvh] w-full max-w-6xl flex-col overflow-hidden rounded-none bg-white shadow-lg sm:h-auto sm:max-h-[92vh] sm:rounded-xl"
        onMouseDown={(e) => e.stopPropagation()}
        style={{ fontFamily: fontBody }}
      >
        <button
          type="button"
          onClick={onClose}
          className={`absolute right-2 top-2 z-30 flex h-9 w-9 items-center justify-center rounded-full ${btnGeneral} text-lg leading-none shadow sm:right-3 sm:top-3 sm:h-8 sm:w-8`}
          aria-label="Close"
          title="Close"
        >
          ×
        </button>

        <div className="flex shrink-0 flex-col gap-3 border-b border-gray-200 px-3 pb-3 pt-4 pr-12 sm:px-6 sm:pt-5 lg:flex-row lg:items-center lg:justify-between lg:gap-4 lg:pr-6">
          <div className="min-w-0 flex-1">
            <div className="text-lg font-semibold" style={{ fontFamily: fontHeader }}>
              {title}
            </div>
            <div className="mt-0.5 text-xs text-gray-600">
              <span className="hidden lg:inline">
                Left pane is the editor. Right pane is output-only PDF preview. Create/Save does not close this modal.
              </span>
              <span className="lg:hidden">
                Edit details, line items, preview and quote tools without leaving this screen.
              </span>
            </div>

            {quoteLocked ? (
              <div className="mt-2 rounded-lg border border-orange-300 bg-orange-50 px-3 py-2 text-[11px] font-semibold text-orange-800">
                Quote is locked by status (<span className="font-mono">{normStatus(quoteStatus) || "unknown"}</span>). Editing is disabled.
              </div>
            ) : null}

            {headerBadges.length > 0 && (
              <div className="mt-2 flex flex-wrap gap-2">
                {headerBadges.map((b) => (
                  <span
                    key={b.k}
                    className="px-2 py-1 rounded-full text-[11px] font-semibold border border-gray-200 bg-gray-50 text-gray-700"
                  >
                    {b.k}: <span className="font-mono">{b.v}</span>
                  </span>
                ))}
              </div>
            )}
          </div>

          <div className="flex w-full flex-wrap items-center gap-2 lg:w-auto lg:shrink-0 lg:justify-end">
            {!quoteId ? (
              <button
                type="button"
                onClick={handleCreateDraft}
                disabled={!nsCanCreateDraft}
                className="flex-1 rounded-full px-4 py-2 text-[12px] font-semibold shadow disabled:opacity-60 lg:hidden"
                style={{
                  background: theme?.button_action?.normal || theme?.button_general?.normal || "#111827",
                  color: "#fff",
                  fontFamily: theme?.font_body || theme?.font_header || "inherit",
                }}
                title="Create quote draft"
              >
                {saving ? "Creating…" : "Create Draft"}
              </button>
            ) : null}

            <button
              type="button"
              onClick={handleSaveChanges}
              disabled={!nsCanSaveChanges}
              className={`${!quoteId ? "hidden lg:inline-flex" : "inline-flex"} flex-1 items-center justify-center rounded-full px-4 py-2 text-[12px] font-semibold shadow disabled:opacity-60 lg:flex-none`}
              style={{
                background: theme?.button_action?.normal || theme?.button_general?.normal || "#111827",
                color: "#fff",
                fontFamily: theme?.font_body || theme?.font_header || "inherit",
              }}
              onMouseEnter={(e) => {
                if (!nsCanSaveChanges) return;
                e.currentTarget.style.background = theme?.button_action?.hover || theme?.button_action?.normal || e.currentTarget.style.background;
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = theme?.button_action?.normal || theme?.button_general?.normal || "#111827";
              }}
              onMouseDown={(e) => {
                if (!nsCanSaveChanges) return;
                e.currentTarget.style.background = theme?.button_action?.click || theme?.button_action?.normal || e.currentTarget.style.background;
              }}
              onMouseUp={(e) => {
                if (!nsCanSaveChanges) return;
                e.currentTarget.style.background = theme?.button_action?.hover || theme?.button_action?.normal || e.currentTarget.style.background;
              }}
              title="Save changes"
            >
              {saving ? "Saving…" : "Save"}
            </button>

            <button
              type="button"
              onClick={handleSaveAndRefresh}
              disabled={!nsCanSaveAndRefresh}
              className={`${!quoteId ? "hidden lg:inline-flex" : "inline-flex"} flex-1 items-center justify-center rounded-full px-4 py-2 text-[12px] font-semibold shadow disabled:opacity-60 lg:flex-none`}
              style={{
                background: theme?.button_action?.normal || theme?.button_general?.normal || "#111827",
                color: "#fff",
                fontFamily: theme?.font_body || theme?.font_header || "inherit",
              }}
              onMouseEnter={(e) => {
                if (!nsCanSaveAndRefresh) return;
                e.currentTarget.style.background = theme?.button_action?.hover || theme?.button_action?.normal || e.currentTarget.style.background;
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = theme?.button_action?.normal || theme?.button_general?.normal || "#111827";
              }}
              onMouseDown={(e) => {
                if (!nsCanSaveAndRefresh) return;
                e.currentTarget.style.background = theme?.button_action?.click || theme?.button_action?.normal || e.currentTarget.style.background;
              }}
              onMouseUp={(e) => {
                if (!nsCanSaveAndRefresh) return;
                e.currentTarget.style.background = theme?.button_action?.hover || theme?.button_action?.normal || e.currentTarget.style.background;
              }}
              title="Save changes and refresh PDF preview"
            >
              Save & Refresh
            </button>

	            <span
	              className={`shrink-0 rounded-full border px-3 py-1 text-[11px] font-extrabold ${
	                saving
	                  ? "border-gray-300 bg-gray-100 text-gray-700"
	                  : previewStale
	                  ? "border-orange-300 bg-orange-50 text-orange-800"
	                  : "border-green-300 bg-green-50 text-green-800"
	              }`}
	              title={previewStale ? "Changes require preview refresh" : "Up to date"}
	            >
	              {saving ? "Saving…" : previewStale ? "Dirty" : "Saved"}
	            </span>
          </div>
        </div>
        <div className="min-h-0 flex-1 overflow-x-hidden overflow-y-auto overscroll-contain">
          <div className="px-3 pb-6 pt-3 sm:px-6 sm:pt-4">
            {saveErr ? (
              <div className="mb-3 break-words rounded-lg border border-red-300 bg-red-50 px-3 py-2 text-xs text-red-800">
                {saveErr}
              </div>
            ) : null}

            <div className="sticky top-0 z-20 -mx-3 mb-3 border-y border-gray-200 bg-white/95 px-3 py-2 backdrop-blur lg:hidden">
              <div className="grid grid-cols-4 gap-1 rounded-xl bg-gray-100 p-1">
                {[
                  ["details", "Details"],
                  ["items", "Items"],
                  ["preview", previewStale ? "Preview •" : "Preview"],
                  ["more", "More"],
                ].map(([key, label]) => {
                  const active = mobileWorkspace === key;
                  return (
                    <button
                      key={key}
                      type="button"
                      onClick={() => setMobileWorkspace(key)}
                      className={`min-w-0 rounded-lg px-1.5 py-2 text-[11px] font-extrabold transition ${
                        active ? "bg-white text-gray-900 shadow-sm" : `${btnGeneral} shadow-sm`
                      }`}
                    >
                      <span className="block truncate">{label}</span>
                    </button>
                  );
                })}
              </div>
            </div>

            <div className="grid min-w-0 grid-cols-1 gap-4 lg:grid-cols-[1.55fr_1fr]">
              {/* LEFT */}
              <div className={`${mobileWorkspace === "more" ? "hidden" : "flex"} min-h-0 min-w-0 flex-col gap-4 lg:flex`}>
                <div className={`${mobileWorkspace === "details" ? "block" : "hidden"} min-w-0 overflow-hidden rounded-lg border border-gray-200 bg-white p-3 sm:p-4 lg:block`}>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    {/* ADD: Title */}
                    <div>
                      <label className={labelCls}>Title</label>
                      <input
                        value={quoteTitle}
                        onChange={(e) => {
                          setQuoteTitle(e.target.value);
                          markDirtyIfEditing();
                        }}
                        className={fieldCls}
                        placeholder="e.g., CCTV Survey – Colready Farm – Feb 2026"
                        disabled={quoteLocked}
                      />
                      <div className="mt-1 text-[11px] text-gray-600">
                        Used for inbox/list summary. Optional, but strongly recommended.
                      </div>

                      {/* Quote Type Toggle */}
                      <div className="mt-3">
                        <div className="inline-flex items-center gap-2">
                          <label
                            htmlFor={switchId}
                            className="text-slate-600 text-[11px] font-semibold cursor-pointer select-none"
                          >
                            Standard
                          </label>

                          <div className="relative inline-block w-11 h-5">
                            <input
                              id={switchId}
                              type="checkbox"
                              checked={!!multiOptionEnabled}
                              onChange={(e) => {
                                setMultiOptionEnabled(e.target.checked);
                                markDirtyIfEditing();
                              }}
                              className="peer appearance-none w-11 h-5 rounded-full cursor-pointer transition-colors duration-300"
                              style={{
                                backgroundColor: multiOptionEnabled ? (theme?.primary_color || "#172d41") : "#c2c1c1",
                              }}
                              disabled={quoteLocked}
                            />
                            <label
                              htmlFor={switchId}
                              className="absolute top-0 left-0 w-5 h-5 bg-white rounded-full border shadow-sm transition-transform duration-300 peer-checked:translate-x-6 cursor-pointer"
                              style={{
                                borderColor: multiOptionEnabled ? (theme?.primary_color || "#172d41") : "#c2c1c1",
                              }}
                            />
                          </div>

                          <label
                            htmlFor={switchId}
                            className="text-slate-600 text-[11px] font-semibold cursor-pointer select-none"
                          >
                            Multi-Option
                          </label>
                        </div>

                        <div className="mt-1 text-[11px] text-gray-600">
                          {multiOptionEnabled ? "Multi-option quote mode enabled." : "Standard quote mode enabled."}
                        </div>
                      </div>
                    </div>

                    <div>
                      <label className={labelCls}>Customer</label>
                      <select
                        value={customerId}
                        onChange={(e) => {
                          setCustomerId(e.target.value);
                          markDirty();
                        }}
                        className={fieldCls}
                        disabled={quoteLocked}
                      >
                        <option value="">Select customer…</option>
                        {customers.map((c) => (
                          <option key={c.id} value={c.id}>
                            {c.name} (ID {c.id})
                          </option>
                        ))}
                      </select>
                    </div>

                    <div>
                      <label className={labelCls}>Contact (filtered by customer)</label>
                      <select
                        value={contactId}
                        onChange={(e) => {
                          setContactId(e.target.value);
                          markDirty();
                        }}
                        className={fieldCls}
                        disabled={quoteLocked || !toIntOrNull(customerId) || contactsBusy}
                      >
                        <option value="">
                          {contactsBusy
                            ? "Loading contacts…"
                            : !toIntOrNull(customerId)
                            ? "Select customer first…"
                            : contacts.length
                            ? "Select contact…"
                            : "No contacts for this customer"}
                        </option>
                        {contactOptions.map((c) => (
                          <option key={c?.id} value={String(c?.id ?? "")}>
                            {contactLabel(c)}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div>
                      <label className={labelCls}>Email (from contact)</label>
                      <input value={toEmail} readOnly className="w-full border border-gray-300 rounded px-3 py-2 text-xs bg-gray-100" />
                    </div>

                    <div>
                      <label className={labelCls}>Opportunity (optional — open for customer/contact)</label>
                      <select
                        value={norm(effectiveOpportunityId) ? String(effectiveOpportunityId) : ""}
                        onChange={(e) => {
                          setOpportunityId(e.target.value);
                          markDirty();
                        }}
                        className={fieldCls}
                        disabled={quoteLocked || !toIntOrNull(customerId) || oppsBusy}
                      >
                        <option value="">
                          {oppsBusy ? "Loading opportunities…" : !toIntOrNull(customerId) ? "Select customer first…" : "No opportunity (leave blank)"}
                        </option>

                        {oppOptions.length > 0 && (
                          <option value="" disabled>
                            ── Open opportunities ──
                          </option>
                        )}

                        {oppOptions.map((o) => (
                          <option key={o.id} value={o.id}>
                            {o.label}
                          </option>
                        ))}
                      </select>

                      <div className="mt-1 text-[11px] text-gray-600">
                        {toIntOrNull(customerId) ? (
                          oppsBusy ? (
                            "Loading open opportunities…"
                          ) : (
                            <>
                              Open opportunities: <span className="font-semibold">{openOppsSummary.count}</span> • Total:{" "}
                              <span className="font-semibold">{money(openOppsSummary.total, currency)}</span>
                              {DEBUG ? (
                                <span className="ml-2 text-gray-500">
                                  • Loaded: <span className="font-mono">{rawOppsCount}</span> • Matched:{" "}
                                  <span className="font-mono">{matchedOppsCount}</span>
                                  {lastOppsFetchErr ? (
                                    <>
                                      {" "}
                                      • Fetch error: <span className="font-mono text-red-700">{lastOppsFetchErr}</span>
                                    </>
                                  ) : null}
                                </span>
                              ) : null}
                            </>
                          )
                        ) : (
                          "Select a customer to load opportunities."
                        )}
                      </div>
                    </div>

                    {/* TERMS */}
                    <div>
                      <label className={labelCls}>Quote Terms Document (optional)</label>
                      <select
                        value={quoteTermsIdMode.toSelectValue(quoteTermsDocumentId)}
                        onChange={(e) => {
                          const parsed = quoteTermsIdMode.parse(e.target.value);
                          setQuoteTermsDocumentId(parsed);
                          markDirty();
                        }}
                        className={fieldCls}
                        disabled={quoteLocked || quoteTermsBusy}
                      >
                        <option value="">
                          {quoteTermsBusy ? "Loading terms…" : quoteTermsDocs.length ? "No quote terms (leave blank)" : "No terms documents found"}
                        </option>
                        {quoteTermsDocs.map((t) => (
                          <option key={String(t?.id)} value={quoteTermsIdMode.toSelectValue(t?.id)}>
                            {termLabel(t)}
                          </option>
                        ))}
                      </select>
                      {quoteTermsErr ? <div className="mt-1 text-[11px] text-red-700">{quoteTermsErr}</div> : null}
                    </div>

                    <div>
                      <label className={labelCls}>Service Terms Document (optional)</label>
                      <select
                        value={serviceTermsIdMode.toSelectValue(serviceTermsDocumentId)}
                        onChange={(e) => {
                          const parsed = serviceTermsIdMode.parse(e.target.value);
                          setServiceTermsDocumentId(parsed);
                          markDirty();
                        }}
                        className={fieldCls}
                        disabled={quoteLocked || serviceTermsBusy}
                      >
                        <option value="">
                          {serviceTermsBusy ? "Loading terms…" : serviceTermsDocs.length ? "No service terms (leave blank)" : "No terms documents found"}
                        </option>
                        {serviceTermsDocs.map((t) => (
                          <option key={String(t?.id)} value={serviceTermsIdMode.toSelectValue(t?.id)}>
                            {termLabel(t)}
                          </option>
                        ))}
                      </select>
                      {serviceTermsErr ? <div className="mt-1 text-[11px] text-red-700">{serviceTermsErr}</div> : null}
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3 md:col-span-2">
                      <div>
                        <label className={`${labelCls} min-h-[28px]`}>Currency</label>
                        <select
                          value={currency}
                          onChange={(e) => {
                            setCurrency(e.target.value);
                            markDirty();
                          }}
                          className={fieldCls}
                          disabled={quoteLocked}
                        >
                          <option value="GBP">GBP</option>
                          <option value="EUR">EUR</option>
                          <option value="USD">USD</option>
                        </select>
                      </div>

                      <div>
                        <label className={`${labelCls} min-h-[28px]`}>Opportunity ID override (optional)</label>
                        <input
                          value={opportunityId}
                          onChange={(e) => {
                            setOpportunityId(e.target.value);
                            markDirty();
                          }}
                          className={fieldCls}
                          inputMode="numeric"
                          placeholder="blank allowed"
                          disabled={quoteLocked}
                        />
                      </div>
                    </div>
                  </div>

                  <div className="mt-3 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                    <div className="text-[11px] text-gray-600">
                      Terms keys: <span className="font-mono">{QUOTE_TERMS_KEY}</span> / <span className="font-mono">{SERVICE_TERMS_KEY}</span>
                      {DEBUG ? (
                        <span className="ml-2 text-gray-500">
                          • id modes: <span className="font-mono">{quoteTermsIdMode.mode}</span> /{" "}
                          <span className="font-mono">{serviceTermsIdMode.mode}</span>
                        </span>
                      ) : null}
                    </div>

                    <button
                      type="button"
                      onClick={() => loadTermsLists({ include_inactive: false })}
                      className={`${btnGeneral} text-[11px] font-semibold py-2 px-3 rounded-full shadow disabled:opacity-60`}
                      disabled={quoteLocked || quoteTermsBusy || serviceTermsBusy}
                      title="Reload terms documents from the server"
                    >
                      {quoteTermsBusy || serviceTermsBusy ? "Loading…" : "Reload Terms"}
                    </button>
                  </div>
                </div>

                {/* ─── Line Items section ──────────────────────────────────────────── */}
                <div className={`${mobileWorkspace === "items" ? "block" : "hidden"} min-w-0 overflow-visible border-0 bg-white lg:block lg:overflow-hidden lg:rounded-lg lg:border lg:border-gray-200`}>
                  <div className="flex items-center justify-between gap-3 border-b border-gray-200 bg-gray-50 px-3 py-3 sm:px-4">
                    <div className="min-w-0">
                      <div className="text-xs font-semibold text-gray-800">Line Items</div>
                      <div className="text-[11px] text-gray-600">
                        {quoteId ? (
                          <>
                            Server-backed via <span className="font-mono">/api/quotes/{quoteId}/line-items</span> • Editable:{" "}
                            <span className="font-semibold">{liEditable ? "Yes (draft/submitted)" : "No (locked)"}</span>
                          </>
                        ) : (
                          "Draft-only until the quote is created. After create, items are saved via the API."
                        )}
                      </div>
                      {liErr ? <div className="mt-1 text-[11px] text-red-700 font-semibold">{liErr}</div> : null}
                    </div>
                  </div>

                  <div className="grid grid-cols-3 gap-1 border-b border-gray-200 bg-white p-2 lg:hidden">
                    {[
                      ["entry", multiOptionEnabled ? "Add" : "Manual"],
                      ["pricebook", "Pricebook"],
                      ["current", multiOptionEnabled ? "Option Items" : "Current"],
                    ].map(([key, label]) => {
                      const active = mobileItemsPane === key;
                      return (
                        <button
                          key={key}
                          type="button"
                          onClick={() => setMobileItemsPane(key)}
                          className={`rounded-lg px-2 py-2 text-[11px] font-extrabold ${
                            active ? "bg-gray-900 text-white shadow-sm" : "bg-gray-100 text-gray-700"
                          }`}
                        >
                          {label}
                        </button>
                      );
                    })}
                  </div>

                  {/*
                    FIX 3: The ternary now correctly shows EITHER the standard editor
                    OR the multi-option panel — they were previously overlapping due to
                    the multi-option panel being nested inside the truthy branch's pbItems list.
                  */}
                  {!multiOptionEnabled ? (
                    /* ── Standard line-items editor ── */
                    <div className="flex flex-col gap-3 p-0 lg:p-4">
                      {/* Manual add */}
                      <div className={`${mobileItemsPane === "entry" ? "block" : "hidden"} overflow-hidden rounded-none border-0 bg-white lg:block lg:rounded-lg lg:border lg:border-gray-200`}>
                        <div className="flex items-center justify-between border-b border-gray-200 bg-gray-50 px-3 py-2 text-[11px] font-semibold text-gray-700">
                          <span>Manual item</span>
                          <span className="text-[11px] text-gray-500">
                            {quoteId ? (liEditable ? "Editable" : "Locked") : "Draft"}
                          </span>
                        </div>

                        <div className="grid grid-cols-1 items-end gap-2 p-3 lg:grid-cols-[1fr_100px_140px_120px_auto]">
                          <div>
                            <label className={labelCls}>Description</label>
                            <input
                              value={manualDesc}
                              onChange={(e) => setManualDesc(e.target.value)}
                              className={fieldCls}
                              placeholder="e.g. Camera at Junction 9"
                              disabled={!!quoteId && (!liEditable || liDraftAdding)}
                            />
                          </div>

                          <div>
                            <label className={labelCls}>Qty</label>
                            <input
                              value={manualQty}
                              onChange={(e) => setManualQty(e.target.value)}
                              className={fieldCls}
                              inputMode="numeric"
                              disabled={!!quoteId && (!liEditable || liDraftAdding)}
                            />
                          </div>

                          <div>
                            <label className={labelCls}>Unit Price</label>
                            <input
                              value={manualUnitPrice}
                              onChange={(e) => setManualUnitPrice(e.target.value)}
                              className={fieldCls}
                              inputMode="decimal"
                              placeholder="0.00"
                              disabled={!!quoteId && (!liEditable || liDraftAdding)}
                            />
                          </div>

                          <div>
                            <label className={labelCls}>VAT %</label>
                            <select
                              value={manualVatPct}
                              onChange={(e) => setManualVatPct(e.target.value)}
                              className={fieldCls}
                              disabled={!!quoteId && (!liEditable || liDraftAdding)}
                            >
                              <option value="0">0</option>
                              <option value="5">5</option>
                              <option value="20">20</option>
                            </select>
                          </div>

                          <button
                            type="button"
                            onClick={handleAddManualItem}
                            disabled={!!quoteId && (!liEditable || liDraftAdding)}
                            className={`${btnAction} w-full rounded-full px-4 py-2 text-xs font-semibold shadow disabled:opacity-60 md:w-auto`}
                            title={quoteId && !liEditable ? "Line items locked by status" : "Add item"}
                          >
                            {liDraftAdding ? "Adding…" : "Add"}
                          </button>
                        </div>
                      </div>

                      {/* Current manual/API line items */}
                      <div className={`${mobileItemsPane === "current" ? "block" : "hidden"} overflow-hidden rounded-none border-0 bg-white lg:block lg:rounded-lg lg:border lg:border-gray-200`}>
                        <div className="flex items-center justify-between border-b border-gray-200 bg-gray-50 px-3 py-2 text-[11px] font-semibold text-gray-700">
                          <span>Current Line Items</span>
                          <span className="text-[11px] text-gray-500">
                            {liBusy ? "Loading..." : `${Array.isArray(lineItems) ? lineItems.length : 0} item(s)`}
                          </span>
                        </div>

                        <div className="space-y-3 p-3 lg:hidden">
                          {liBusy ? (
                            <div className="py-4 text-center text-xs text-gray-500">Loading line items...</div>
                          ) : !Array.isArray(lineItems) || lineItems.length === 0 ? (
                            <div className="py-4 text-center text-xs text-gray-500">No line items added yet.</div>
                          ) : (
                            lineItems.map((li, idx) => {
                              const liId = li?.id;
                              const busy = liCommitBusyId === liId;
                              const desc = lineDesc(li);
                              const qty = lineQty(li);
                              const unit = lineUnitPrice(li);
                              const vatPct = Math.round(rateToPct(lineTaxRate(li)));
                              const total = calcLineTotal(li);

                              return (
                                <div key={`mobile-${liId || `draft-${idx}`}`} className="rounded-xl border border-gray-200 bg-white p-3">
                                  <div className="mb-3 flex items-start justify-between gap-3">
                                    <div className="text-xs font-extrabold text-gray-700">Item {idx + 1}</div>
                                    <div className="shrink-0 text-xs font-extrabold text-gray-900">{money(total, currency)}</div>
                                  </div>
                                  <div>
                                    <label className={labelCls}>Description</label>
                                    <input
                                      value={desc}
                                      onChange={(e) => updateLineItemLocal(idx, { description: e.target.value, name: e.target.value })}
                                      onBlur={(e) => {
                                        if (!quoteId || !liId) return;
                                        commitLineItemPatch(liId, { description: e.target.value });
                                      }}
                                      className={fieldCls}
                                      disabled={!!quoteId && (!liEditable || busy)}
                                    />
                                  </div>
                                  <div className="mt-3 grid grid-cols-3 gap-2">
                                    <div>
                                      <label className={labelCls}>Qty</label>
                                      <input
                                        value={qty}
                                        onChange={(e) => updateLineItemLocal(idx, { qty: e.target.value, quantity: e.target.value })}
                                        onBlur={(e) => {
                                          if (!quoteId || !liId) return;
                                          commitLineItemPatch(liId, { quantity: e.target.value });
                                        }}
                                        className={fieldCls}
                                        inputMode="numeric"
                                        disabled={!!quoteId && (!liEditable || busy)}
                                      />
                                    </div>
                                    <div>
                                      <label className={labelCls}>Unit</label>
                                      <input
                                        value={unit}
                                        onChange={(e) => updateLineItemLocal(idx, { unit_price: e.target.value })}
                                        onBlur={(e) => {
                                          if (!quoteId || !liId) return;
                                          commitLineItemPatch(liId, { unit_price: e.target.value });
                                        }}
                                        className={fieldCls}
                                        inputMode="decimal"
                                        disabled={!!quoteId && (!liEditable || busy)}
                                      />
                                    </div>
                                    <div>
                                      <label className={labelCls}>VAT %</label>
                                      <select
                                        value={String(vatPct)}
                                        onChange={(e) => {
                                          const rate = pctToRate(e.target.value);
                                          updateLineItemLocal(idx, { tax_rate: rate });
                                          if (quoteId && liId) commitLineItemPatch(liId, { tax_rate: rate });
                                        }}
                                        className={fieldCls}
                                        disabled={!!quoteId && (!liEditable || busy)}
                                      >
                                        <option value="0">0</option>
                                        <option value="5">5</option>
                                        <option value="20">20</option>
                                      </select>
                                    </div>
                                  </div>
                                  <button
                                    type="button"
                                    onClick={() => {
                                      if (!quoteId) {
                                        removeLineItemLocal(idx);
                                        return;
                                      }
                                      deleteLineItem(liId);
                                    }}
                                    disabled={!!quoteId && (!liEditable || busy)}
                                    className={`${btnGeneral} mt-3 w-full rounded-full px-3 py-2 text-[11px] font-semibold shadow disabled:opacity-60`}
                                    title={quoteId && !liEditable ? "Line items locked by status" : "Delete line item"}
                                  >
                                    {busy ? "..." : "Delete"}
                                  </button>
                                </div>
                              );
                            })
                          )}
                        </div>

                        <div className="hidden overflow-x-auto lg:block">
                          <table className="min-w-full text-[11px]">
                            <thead className="bg-white border-b border-gray-200 text-gray-600">
                              <tr>
                                <th className="text-left px-3 py-2 font-semibold">#</th>
                                <th className="text-left px-3 py-2 font-semibold">Description</th>
                                <th className="text-left px-3 py-2 font-semibold">Qty</th>
                                <th className="text-left px-3 py-2 font-semibold">Unit Price</th>
                                <th className="text-left px-3 py-2 font-semibold">VAT %</th>
                                <th className="text-left px-3 py-2 font-semibold">Total</th>
                                <th className="text-right px-3 py-2 font-semibold">Actions</th>
                              </tr>
                            </thead>

                            <tbody>
                              {liBusy ? (
                                <tr>
                                  <td colSpan={7} className="px-3 py-4 text-center text-gray-500">
                                    Loading line items...
                                  </td>
                                </tr>
                              ) : !Array.isArray(lineItems) || lineItems.length === 0 ? (
                                <tr>
                                  <td colSpan={7} className="px-3 py-4 text-center text-gray-500">
                                    No line items added yet.
                                  </td>
                                </tr>
                              ) : (
                                lineItems.map((li, idx) => {
                                  const liId = li?.id;
                                  const busy = liCommitBusyId === liId;
                                  const desc = lineDesc(li);
                                  const qty = lineQty(li);
                                  const unit = lineUnitPrice(li);
                                  const vatPct = Math.round(rateToPct(lineTaxRate(li)));
                                  const total = calcLineTotal(li);

                                  return (
                                    <tr key={liId || `draft-${idx}`} className="border-b border-gray-100 last:border-b-0">
                                      <td className="px-3 py-2 text-gray-500">{idx + 1}</td>

                                      <td className="px-3 py-2 min-w-[240px]">
                                        <input
                                          value={desc}
                                          onChange={(e) =>
                                            updateLineItemLocal(idx, {
                                              description: e.target.value,
                                              name: e.target.value,
                                            })
                                          }
                                          onBlur={(e) => {
                                            if (!quoteId || !liId) return;
                                            commitLineItemPatch(liId, { description: e.target.value });
                                          }}
                                          className={fieldCls}
                                          disabled={!!quoteId && (!liEditable || busy)}
                                        />
                                      </td>

                                      <td className="px-3 py-2 w-[90px]">
                                        <input
                                          value={qty}
                                          onChange={(e) =>
                                            updateLineItemLocal(idx, {
                                              qty: e.target.value,
                                              quantity: e.target.value,
                                            })
                                          }
                                          onBlur={(e) => {
                                            if (!quoteId || !liId) return;
                                            commitLineItemPatch(liId, { quantity: e.target.value });
                                          }}
                                          className={fieldCls}
                                          inputMode="numeric"
                                          disabled={!!quoteId && (!liEditable || busy)}
                                        />
                                      </td>

                                      <td className="px-3 py-2 w-[140px]">
                                        <input
                                          value={unit}
                                          onChange={(e) => updateLineItemLocal(idx, { unit_price: e.target.value })}
                                          onBlur={(e) => {
                                            if (!quoteId || !liId) return;
                                            commitLineItemPatch(liId, { unit_price: e.target.value });
                                          }}
                                          className={fieldCls}
                                          inputMode="decimal"
                                          disabled={!!quoteId && (!liEditable || busy)}
                                        />
                                      </td>

                                      <td className="px-3 py-2 w-[110px]">
                                        <select
                                          value={String(vatPct)}
                                          onChange={(e) => {
                                            const rate = pctToRate(e.target.value);
                                            updateLineItemLocal(idx, { tax_rate: rate });
                                            if (quoteId && liId) commitLineItemPatch(liId, { tax_rate: rate });
                                          }}
                                          className={fieldCls}
                                          disabled={!!quoteId && (!liEditable || busy)}
                                        >
                                          <option value="0">0</option>
                                          <option value="5">5</option>
                                          <option value="20">20</option>
                                        </select>
                                      </td>

                                      <td className="px-3 py-2 font-semibold whitespace-nowrap">
                                        {money(total, currency)}
                                      </td>

                                      <td className="px-3 py-2 text-right">
                                        <button
                                          type="button"
                                          onClick={() => {
                                            if (!quoteId) {
                                              removeLineItemLocal(idx);
                                              return;
                                            }
                                            deleteLineItem(liId);
                                          }}
                                          disabled={!!quoteId && (!liEditable || busy)}
                                          className={`${btnGeneral} text-[11px] font-semibold py-1.5 px-3 rounded-full shadow disabled:opacity-60`}
                                          title={quoteId && !liEditable ? "Line items locked by status" : "Delete line item"}
                                        >
                                          {busy ? "..." : "Delete"}
                                        </button>
                                      </td>
                                    </tr>
                                  );
                                })
                              )}
                            </tbody>
                          </table>
                        </div>

                        <div className="flex flex-wrap items-center justify-end gap-x-4 gap-y-1 border-t border-gray-200 bg-gray-50 px-3 py-2 text-[11px] text-gray-700">
                          <span>Subtotal: <strong>{money(totals.subtotal, currency)}</strong></span>
                          <span>VAT: <strong>{money(totals.vatTotal, currency)}</strong></span>
                          <span>Total: <strong>{money(totals.grandTotal, currency)}</strong></span>
                        </div>
                      </div>

                      {/* Pricebook dropdown + items */}
                      <div className={`${mobileItemsPane === "pricebook" ? "block" : "hidden"} overflow-hidden rounded-none border-0 bg-white lg:block lg:rounded-lg lg:border lg:border-gray-200`}>
                        <div className="flex items-center justify-between gap-3 border-b border-gray-200 bg-gray-50 px-3 py-2 text-[11px] font-semibold text-gray-700">
                          <span>Pricebook</span>
                          <button
                            type="button"
                            onClick={loadEligiblePricebooks}
                            disabled={quoteLocked || pricebooksBusy}
                            className={`${btnGeneral} text-[11px] font-semibold py-1.5 px-3 rounded-full shadow disabled:opacity-60`}
                            title="Reload eligible pricebooks"
                          >
                            {pricebooksBusy ? "Loading…" : "Reload"}
                          </button>
                        </div>

                        <div className="p-3 grid grid-cols-1 md:grid-cols-[1fr_auto] gap-2 items-end">
                          <div>
                            <label className={labelCls}>Select Pricebook</label>
                            <select
                              value={selectedPricebookId}
                              onChange={(e) => setSelectedPricebookId(e.target.value)}
                              className={fieldCls}
                              disabled={quoteLocked || pricebooksBusy}
                            >
                              <option value="">
                                {pricebooksBusy
                                  ? "Loading…"
                                  : eligiblePricebooks.length
                                  ? toIntOrNull(customerId)
                                    ? "Select pricebook (customer/resource/tenant)…"
                                    : "Select pricebook (tenant-wide only until customer selected)…"
                                  : "No eligible pricebooks"}
                              </option>
                              {eligiblePricebooks.map((pb) => (
                                <option key={String(pb?.id)} value={String(pb?.id ?? "")}>
                                  {pbLabel(pb)}
                                </option>
                              ))}
                            </select>
                            {pricebooksErr ? <div className="mt-1 text-[11px] text-red-700">{pricebooksErr}</div> : null}
                            {DEBUG ? (
                              <div className="mt-1 text-[11px] text-gray-500">
                                Raw PBs: <span className="font-mono">{Array.isArray(pricebooks) ? pricebooks.length : 0}</span> • Eligible:{" "}
                                <span className="font-mono">{eligiblePricebooks.length}</span> • Customer:{" "}
                                <span className="font-mono">{toIntOrNull(customerId) ? customerId : "—"}</span>
                              </div>
                            ) : null}
                          </div>

                          <button
                            type="button"
                            onClick={() => loadPricebookItems(selectedPricebookId)}
                            disabled={quoteLocked || !selectedPricebookId || pbItemsBusy}
                            className={`${btnGeneral} w-full rounded-full px-4 py-2 text-xs font-semibold shadow disabled:opacity-60 md:w-auto`}
                            title={!selectedPricebookId ? "Select a pricebook first" : "Reload items"}
                          >
                            {pbItemsBusy ? "Loading…" : "Load Items"}
                          </button>
                        </div>

                        {pbItemsErr ? <div className="px-3 pb-3 text-[11px] text-red-700 font-semibold">{pbItemsErr}</div> : null}

                        {selectedPricebookId ? (
                          <div className="px-3 pb-3">
                            <div className="text-[11px] text-gray-600 mb-2">
                              Items: <span className="font-semibold">{Array.isArray(pbItems) ? pbItems.length : 0}</span>
                            </div>

                            <div className="overflow-hidden border-0 lg:rounded-lg lg:border lg:border-gray-200">
                              <div className="bg-white px-3 py-2 text-[11px] font-semibold text-gray-700 border-b border-gray-200">
                                Pricebook Items
                              </div>
                              <div className="max-h-[160px] overflow-auto bg-gray-50">
                                {pbItemsBusy ? (
                                  <div className="px-3 py-3 text-[11px] text-gray-500">Loading items…</div>
                                ) : !pbItems.length ? (
                                  <div className="px-3 py-3 text-[11px] text-gray-500">No items in this pricebook.</div>
                                ) : (
                                  /* FIX 2: was `pricebookItems` (undefined) — correct name is `pbItems` */
                                  pbItems.map((it) => (
                                    <button
                                      key={String(it?.id ?? Math.random())}
                                      type="button"
                                      onClick={() => addPricebookItem(it)}
                                      disabled={!!quoteId && !liEditable}
                                      className="w-full text-left px-3 py-2 border-b border-gray-200 last:border-b-0 hover:bg-white text-[11px] disabled:opacity-60 disabled:cursor-not-allowed"
                                    >
                                      <span className="font-semibold text-gray-800">{pbItemLabel(it)}</span>
                                      <span className="ml-2 text-gray-500">
                                        {money(it?.unit_price ?? it?.price ?? 0, currency)} • VAT{" "}
                                        {Math.round(rateToPct(resolveCatalogVatRate(it)))}%
                                      </span>
                                    </button>
                                  ))
                                )}
                              </div>
                            </div>
                          </div>
                        ) : null}
                      </div>
                    </div>
                  ) : (
                    /* ── Multi-option panel (FIX 3: now correctly the : else branch) ── */
                    <div className="p-0 lg:p-4">
                      <div className="flex flex-col gap-1 px-3 py-3 sm:flex-row sm:items-center sm:justify-between lg:px-0 lg:pt-0">
                        <div className="font-semibold">Options</div>
                        <div className="text-sm text-gray-600">
                          In Multi-option mode legacy line items are disabled
                        </div>
                      </div>

                      <div className="mb-4 grid grid-cols-3 gap-1 lg:flex lg:flex-wrap lg:gap-3">
                        {(Array.isArray(quoteOptions) ? quoteOptions : []).map((opt) => {
                          const isActive = opt?.id === selectedOptionId;
                          const label = opt?.label || opt?.option_key || `Option ${opt?.option_key || ""}`;
                          const computedTotals = getOptionTotals(opt?.id);
                          const total = Number(computedTotals?.grand_total ?? computedTotals?.total ?? 0) || 0;

                          return (
                            <button
                              key={opt?.id}
                              type="button"
                              onClick={() => setSelectedOptionId(opt?.id)}
                              className={`w-full rounded-lg border px-2 py-2 text-left transition lg:min-w-[220px] lg:flex-1 lg:px-4 lg:py-3 ${
                                isActive ? "border-gray-700 shadow-sm" : "border-gray-200 hover:border-gray-300"
                              }`}
                              style={{
                                background: isActive ? "rgba(0,0,0,0.03)" : "#fff",
                              }}
                            >
                              <div className="truncate text-[11px] font-semibold text-gray-600 sm:text-sm lg:whitespace-normal">{label}</div>
                              <div className="mt-0.5 text-[10px] text-gray-600 sm:text-xs lg:text-sm">{formatMoney(total)}</div>
                            </button>
                          );
                        })}
                      </div>

                      <div className={`${mobileItemsPane === "current" ? "hidden" : "grid"} mb-4 grid-cols-1 gap-4 lg:grid lg:grid-cols-3`}>
                        <div className={`${mobileItemsPane === "entry" ? "block" : "hidden"} border-0 p-3 lg:col-span-2 lg:block lg:rounded-lg lg:border`}>
                          <div className="font-semibold mb-2">Add Line Item</div>
                          <div className="grid grid-cols-1 items-end gap-3 lg:grid-cols-[minmax(120px,1fr)_minmax(40px,50px)_minmax(80px,100px)_minmax(60px,80px)]">
                            <div>
                              <label className="block text-xs text-gray-600 mb-1">Description</label>
                              <input
                                className="w-full border rounded px-3 py-2"
                                value={optLiDescription}
                                onChange={(e) => setOptLiDescription(e.target.value)}
                                placeholder="e.g. Traffic management"
                              />
                            </div>

                            <div>
                              <label className="block text-xs text-gray-600 mb-1">Qty</label>
                              <input
                                className="w-full border rounded px-3 py-2"
                                type="number"
                                value={optLiQty}
                                onChange={(e) => setOptLiQty(e.target.value)}
                              />
                            </div>

                            <div>
                              <label className="block text-xs text-gray-600 mb-1">Unit Price</label>
                              <input
                                className="w-full border rounded px-3 py-2"
                                type="number"
                                value={optLiUnitPrice}
                                onChange={(e) => setOptLiUnitPrice(e.target.value)}
                              />
                            </div>

                            <div>
                              <label className="block text-xs text-gray-600 mb-1">VAT Rate</label>
                              <select
                                className="w-full border rounded px-3 py-2 bg-white"
                                value={String(Math.round(rateToPct(optLiVatRate)))}
                                onChange={(e) => setOptLiVatRate(pctToRate(e.target.value))}
                              >
                                <option value="0">0%</option>
                                <option value="5">5%</option>
                                <option value="20">20%</option>
                              </select>
                            </div>
                          </div>

                          <div className="mt-3 flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
                            <div className="flex-1">
                              <label className="block text-xs text-gray-600 mb-1">Selected option label</label>
                              <input
                                className="w-full border rounded px-3 py-2 h-10"
                                value={optionLabelDraft}
                                onChange={(e) => setOptionLabelDraft(e.target.value)}
                                onBlur={handleSaveSelectedOptionLabel}
                                placeholder="e.g. Before School Holidays"
                              />
                            </div>

                            <button
                              type="button"
                              className="h-10 w-full rounded-lg px-4 py-2 text-white disabled:opacity-60 sm:w-auto"
                              style={{ background: theme?.button_action?.normal || "#f97316" }}
                              onClick={handleAddOptionLineItem}
                              disabled={!selectedOptionId || !String(optLiDescription || "").trim() || optLiSaving}
                            >
                              {optLiSaving ? "Adding…" : "Add Item"}
                            </button>
                          </div>

                          {optLiError ? (
                            <div className="mt-2 text-[11px] text-red-700 font-semibold">{optLiError}</div>
                          ) : null}
                        </div>

                        <div className={`${mobileItemsPane === "pricebook" ? "block" : "hidden"} border-0 p-3 lg:block lg:rounded-lg lg:border`}>
                          <div className="font-semibold mb-2">Pricebook</div>
                          <select
                            className="w-full border rounded px-3 py-2 bg-white"
                            value={selectedPricebookId || ""}
                            onChange={(e) => setSelectedPricebookId(e.target.value ? Number(e.target.value) : null)}
                          >
                            <option value="">Select pricebook…</option>
                            {/* FIX 2: was `pricebooks` which exists but pb.name may differ — use pbLabel for consistency */}
                            {(Array.isArray(pricebooks) ? pricebooks : []).map((pb) => (
                              <option key={pb.id} value={pb.id}>
                                {pbLabel(pb)}
                              </option>
                            ))}
                          </select>

                          <div className="mt-2 border rounded-lg overflow-hidden">
                            <div className="max-h-56 overflow-auto">
                              {!selectedPricebookId ? (
                                <div className="p-3 text-sm text-gray-600">Select a pricebook to browse items.</div>
                              ) : (Array.isArray(pbItems) ? pbItems : []).length === 0 ? (
                                /* FIX 2: was `pricebookItems` (undefined) — correct name is `pbItems` */
                                <div className="p-3 text-sm text-gray-600">No items found for this pricebook.</div>
                              ) : (
                                (Array.isArray(pbItems) ? pbItems : []).map((it) => (
                                  <button
                                    key={it.id}
                                    type="button"
                                    className="w-full text-left px-3 py-2 border-b last:border-b-0 hover:bg-gray-50"
                                    onClick={() => applyPbItemToOptionLineItemForm(it)}
                                  >
                                    <div className="text-sm font-medium">{it.service_name || it.description || it.code}</div>
                                    <div className="text-xs text-gray-600">
                                      {formatMoney(Number(it.unit_price || 0))} • VAT {Math.round(rateToPct(it.tax_rate || 0))}%
                                    </div>
                                  </button>
                                ))
                              )}
                            </div>
                          </div>
                        </div>
                      </div>

                      <div className={`${mobileItemsPane === "current" ? "block" : "hidden"} border-0 p-3 lg:block lg:rounded-lg lg:border`}>
                        <div className="mb-2 flex flex-col gap-1 sm:flex-row sm:items-center sm:justify-between">
                          <div className="font-semibold">Option Line Items</div>
                          {(() => {
                            const sub = (Array.isArray(optionLineItems) ? optionLineItems : []).reduce((acc, li) => {
                              const q = Number(li?.quantity ?? 0) || 0;
                              const u = Number(li?.unit_price ?? 0) || 0;
                              return acc + q * u;
                            }, 0);

                            const vat = (Array.isArray(optionLineItems) ? optionLineItems : []).reduce((acc, li) => {
                              const q = Number(li?.quantity ?? 0) || 0;
                              const u = Number(li?.unit_price ?? 0) || 0;
                              const r = Number(li?.tax_rate ?? 0) || 0;
                              return acc + q * u * r;
                            }, 0);

                            return (
                              <div className="text-sm text-gray-700">
                                Subtotal: <span className="font-semibold">{formatMoney(sub)}</span> &nbsp;•&nbsp; VAT:{" "}
                                <span className="font-semibold">{formatMoney(vat)}</span> &nbsp;•&nbsp; Total:{" "}
                                <span className="font-semibold">{formatMoney(sub + vat)}</span>
                              </div>
                            );
                          })()}
                        </div>

                        <div className="space-y-3 lg:hidden">
                          {(Array.isArray(optionLineItems) ? optionLineItems : []).map((li, idx) => {
                            const q = Number(li?.quantity ?? 0) || 0;
                            const u = Number(li?.unit_price ?? 0) || 0;
                            const r = Number(li?.tax_rate ?? 0) || 0;
                            const total = q * u * (1 + r);

                            return (
                              <div key={`mobile-option-${li.id}`} className="rounded-xl border border-gray-200 bg-white p-3">
                                <div className="mb-3 flex items-start justify-between gap-3">
                                  <div className="text-xs font-extrabold text-gray-700">Item {idx + 1}</div>
                                  <div className="shrink-0 text-xs font-extrabold text-gray-900">{formatMoney(total)}</div>
                                </div>
                                <div>
                                  <label className={labelCls}>Description</label>
                                  <input
                                    className="w-full min-w-0 rounded border px-2 py-2"
                                    value={li.description || ""}
                                    onChange={(e) => {
                                      const v = e.target.value;
                                      setOptionLineItems((prev) =>
                                        (Array.isArray(prev) ? prev : []).map((x) => (x.id === li.id ? { ...x, description: v } : x))
                                      );
                                    }}
                                    onBlur={(e) => handlePatchOptionLineItemField(li.id, { description: e.target.value })}
                                  />
                                </div>
                                <div className="mt-3 grid grid-cols-3 gap-2">
                                  <div>
                                    <label className={labelCls}>Qty</label>
                                    <input
                                      className="w-full min-w-0 rounded border px-2 py-2"
                                      type="number"
                                      step="1"
                                      min="0"
                                      value={li.quantity ?? ""}
                                      onChange={(e) => {
                                        const v = e.target.value;
                                        const n = v === "" ? "" : parseInt(v, 10);
                                        setOptionLineItems((prev) =>
                                          (Array.isArray(prev) ? prev : []).map((x) =>
                                            x.id === li.id ? { ...x, quantity: n === "" || Number.isNaN(n) ? "" : n } : x
                                          )
                                        );
                                      }}
                                      onBlur={(e) => {
                                        const v = e.target.value;
                                        const n = v === "" ? null : parseInt(v, 10);
                                        handlePatchOptionLineItemField(li.id, { quantity: n === null || Number.isNaN(n) ? null : n });
                                      }}
                                    />
                                  </div>
                                  <div>
                                    <label className={labelCls}>Unit</label>
                                    <input
                                      className="w-full min-w-0 rounded border px-2 py-2"
                                      type="number"
                                      value={li.unit_price ?? ""}
                                      onChange={(e) => {
                                        const v = e.target.value;
                                        setOptionLineItems((prev) =>
                                          (Array.isArray(prev) ? prev : []).map((x) => (x.id === li.id ? { ...x, unit_price: v } : x))
                                        );
                                      }}
                                      onBlur={(e) => {
                                        const v = e.target.value;
                                        const n = v === "" ? null : Number(v);
                                        handlePatchOptionLineItemField(li.id, { unit_price: n === null || Number.isNaN(n) ? null : n });
                                      }}
                                    />
                                  </div>
                                  <div>
                                    <label className={labelCls}>VAT</label>
                                    <select
                                      className="w-full min-w-0 rounded border bg-white px-2 py-2"
                                      value={String(Math.round(rateToPct(li.tax_rate)))}
                                      onChange={(e) => {
                                        const rate = pctToRate(e.target.value);
                                        setOptionLineItems((prev) =>
                                          (Array.isArray(prev) ? prev : []).map((x) => (x.id === li.id ? { ...x, tax_rate: rate } : x))
                                        );
                                        handlePatchOptionLineItemField(li.id, { tax_rate: rate });
                                      }}
                                    >
                                      <option value="0">0%</option>
                                      <option value="5">5%</option>
                                      <option value="20">20%</option>
                                    </select>
                                  </div>
                                </div>
                                <button
                                  type="button"
                                  className="mt-3 w-full rounded px-3 py-2 text-white"
                                  style={{ background: theme?.button_general?.normal || theme?.button_action?.normal || "#111827" }}
                                  onClick={() => handleDeleteOptionLineItem(li.id)}
                                >
                                  Delete
                                </button>
                              </div>
                            );
                          })}
                          {(Array.isArray(optionLineItems) ? optionLineItems : []).length === 0 && (
                            <div className="py-4 text-center text-sm text-gray-600">No items for this option yet.</div>
                          )}
                        </div>

                        <div className="hidden overflow-x-auto lg:block">
                          <table className="min-w-full text-sm">
                            <thead>
                              <tr className="text-left text-gray-600">
                                <th className="py-2 pr-3">#</th>
                                <th className="py-2 pr-3">Description</th>
                                <th className="py-2 pr-3">Qty</th>
                                <th className="py-2 pr-3">Unit</th>
                                <th className="py-2 pr-3">VAT</th>
                                <th className="py-2 pr-3">Total</th>
                                <th className="py-2 pr-3">Actions</th>
                              </tr>
                            </thead>
                            <tbody>
                              {(Array.isArray(optionLineItems) ? optionLineItems : []).map((li, idx) => {
                                const q = Number(li?.quantity ?? 0) || 0;
                                const u = Number(li?.unit_price ?? 0) || 0;
                                const r = Number(li?.tax_rate ?? 0) || 0;
                                const total = q * u * (1 + r);

                                return (
                                  <tr key={li.id} className="border-t">
                                    <td className="py-2 pr-3 text-gray-600">{idx + 1}</td>
                                    <td className="py-2 pr-3">
                                      <input
                                        className="w-full border rounded px-2 py-1"
                                        value={li.description || ""}
                                        onChange={(e) => {
                                          const v = e.target.value;
                                          setOptionLineItems((prev) =>
                                            (Array.isArray(prev) ? prev : []).map((x) => (x.id === li.id ? { ...x, description: v } : x))
                                          );
                                        }}
                                        onBlur={(e) => handlePatchOptionLineItemField(li.id, { description: e.target.value })}
                                      />
                                    </td>
                                    <td className="py-2 pr-3">
                                      <input
                                        className="border rounded px-2 py-1"
                                        style={{ width: 92 }}
                                        type="number"
                                        step="1"
                                        min="0"
                                        value={li.quantity ?? ""}
                                        onChange={(e) => {
                                          const v = e.target.value;
                                          const n = v === "" ? "" : parseInt(v, 10);
                                          setOptionLineItems((prev) =>
                                            (Array.isArray(prev) ? prev : []).map((x) =>
                                              x.id === li.id
                                                ? { ...x, quantity: n === "" || Number.isNaN(n) ? "" : n }
                                                : x
                                            )
                                          );
                                        }}
                                        onBlur={(e) => {
                                          const v = e.target.value;
                                          const n = v === "" ? null : parseInt(v, 10);
                                          handlePatchOptionLineItemField(li.id, { quantity: n === null || Number.isNaN(n) ? null : n });
                                        }}
                                      />
                                    </td>
                                    <td className="py-2 pr-3">
                                      <input
                                        className="border rounded px-2 py-1"
                                        style={{ width: 140 }}
                                        type="number"
                                        value={li.unit_price ?? ""}
                                        onChange={(e) => {
                                          const v = e.target.value;
                                          setOptionLineItems((prev) =>
                                            (Array.isArray(prev) ? prev : []).map((x) => (x.id === li.id ? { ...x, unit_price: v } : x))
                                          );
                                        }}
                                        onBlur={(e) => {
                                          const v = e.target.value;
                                          const n = v === "" ? null : Number(v);
                                          handlePatchOptionLineItemField(li.id, { unit_price: n === null || Number.isNaN(n) ? null : n });
                                        }}
                                      />
                                    </td>
                                    <td className="py-2 pr-3">
                                      <select
                                        className="border rounded px-2 py-1 bg-white"
                                        style={{ width: 110 }}
                                        value={String(Math.round(rateToPct(li.tax_rate)))}
                                        onChange={(e) => {
                                          const rate = pctToRate(e.target.value);
                                          setOptionLineItems((prev) =>
                                            (Array.isArray(prev) ? prev : []).map((x) => (x.id === li.id ? { ...x, tax_rate: rate } : x))
                                          );
                                          handlePatchOptionLineItemField(li.id, { tax_rate: rate });
                                        }}
                                      >
                                        <option value="0">0%</option>
                                        <option value="5">5%</option>
                                        <option value="20">20%</option>
                                      </select>
                                    </td>
                                    <td className="py-2 pr-3 font-medium">{formatMoney(total)}</td>
                                    <td className="py-2 pr-3">
                                      <button
                                        type="button"
                                        className="px-3 py-1 rounded text-white"
                                        style={{ background: theme?.button_general?.normal || theme?.button_action?.normal || "#111827" }}
                                        onClick={() => handleDeleteOptionLineItem(li.id)}
                                      >
                                        Delete
                                      </button>
                                    </td>
                                  </tr>
                                );
                              })}
                              {(Array.isArray(optionLineItems) ? optionLineItems : []).length === 0 && (
                                <tr>
                                  <td colSpan={7} className="py-4 text-center text-gray-600">
                                    No items for this option yet.
                                  </td>
                                </tr>
                              )}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    </div>
                  )}
                  {/* ── end line items ternary ── */}
                </div>

                {/* Quote body */}
                <div className={`${mobileWorkspace === "details" ? "block" : "hidden"} min-w-0 overflow-hidden rounded-lg border border-gray-200 bg-white lg:block`}>
                  <div className="flex flex-col gap-2 border-b border-gray-200 bg-gray-50 px-3 py-3 sm:flex-row sm:items-center sm:justify-between sm:px-4">
                    <div>
                      <div className="text-xs font-semibold text-gray-800">Quote Body</div>
                      <div className="text-[11px] text-gray-600">WYSIWYG composer.</div>
                    </div>

                    <button
                      type="button"
                      onClick={() => {
                        if (quoteLocked) return;
                        setBodyHtml((prev) => stripLineItemsBlock(prev));
                        markDirty();
                        onAlert?.("info", "Line items block removed from body (line items remain).");
                      }}
                      disabled={quoteLocked}
                      className={`${btnGeneral} text-[11px] font-semibold py-2 px-3 rounded-full shadow disabled:opacity-60`}
                      title="Remove any legacy embedded line-items table from body"
                    >
                      Remove Table
                    </button>
                  </div>

                  <div className="min-w-0 p-3 sm:p-4">
                    <TiptapEmailEditor
                      value={bodyHtml}
                      onChange={(v) => {
                        if (quoteLocked) return;
                        setBodyHtml(v);
                        markDirty();
                      }}
                      placeholder="Write your quote…"
                    />
                  </div>
                </div>

                {/* Actions */}
                <div className="hidden overflow-hidden rounded-lg border border-gray-200 bg-white lg:block">
                  <div className="flex items-center justify-between gap-3 px-4 py-3">
                    <button type="button" onClick={onClose} className={`${btnGeneral} text-xs font-semibold py-2 px-4 rounded-full shadow`}>
                      Cancel
                    </button>

                    <div className="flex items-center gap-2">
                      {!quoteId ? (
                        <button
                          type="button"
                          onClick={handleCreateDraft}
                          disabled={saving}
                          className={`${btnAction} text-xs font-semibold py-2 px-5 rounded-full shadow`}
                        >
                          {saving ? "Creating…" : "Create Quote (Draft)"}
                        </button>
                      ) : (
                        <>
                          <button
                            type="button"
                            onClick={handleSaveChanges}
                            disabled={saving || !previewStale || quoteLocked}
                            className={`${btnAction} text-xs font-semibold py-2 px-5 rounded-full shadow disabled:opacity-60`}
                          >
                            {saving ? "Saving…" : "Save Changes"}
                          </button>

                          <button
                            type="button"
                            onClick={handleSaveAndRefresh}
                            disabled={saving || !previewStale || quoteLocked}
                            className={`${btnGeneral} text-xs font-semibold py-2 px-5 rounded-full shadow disabled:opacity-60`}
                          >
                            Save & Refresh
                          </button>
                        </>
                      )}
                    </div>
                  </div>
                </div>
              

                {/* PDF Preview (output-only) */}
<div className={`${mobileWorkspace === "preview" ? "flex" : "hidden"} min-w-0 flex-col overflow-hidden border-0 bg-white lg:flex lg:rounded-lg lg:border lg:border-gray-200`}>
                  <div className="flex flex-col gap-1 border-b border-gray-200 bg-gray-50 px-3 py-3 sm:flex-row sm:items-center sm:justify-between sm:px-4">
                    <div>
                      <div className="text-xs font-semibold text-gray-800">PDF Preview</div>
                      <div className="text-[11px] text-gray-600">
                        <span className="hidden lg:inline">Output-only. Create/Save on the left. Refresh preview here.</span>
                        <span className="lg:hidden">Output-only preview. Save controls stay in the modal header.</span>
                      </div>
                    </div>

                    <div className="text-[11px] text-gray-600">{!quoteId ? "Create first" : pdfUrl ? "Ready" : "Not generated"}</div>
                  </div>

                  {quoteId && previewStale ? (
                    <div className="px-4 py-2 text-[11px] font-semibold border-b border-orange-200 bg-orange-50 text-orange-800">
                      Preview out of date — save and refresh.
                    </div>
                  ) : null}

                  <div className="flex flex-col gap-2 border-b border-gray-200 bg-white px-3 py-3 sm:flex-row sm:items-center sm:justify-between sm:px-4">
                    <span
                      className={`px-3 py-1 rounded-full text-[11px] font-extrabold border ${
                        !quoteId
                          ? "border-gray-300 bg-gray-100 text-gray-600"
                          : previewStale
                          ? "border-orange-300 bg-orange-50 text-orange-800"
                          : "border-gray-200 bg-gray-50 text-gray-700"
                      }`}
                    >
                      {!quoteId ? "Not generated" : previewStale ? "Out of date" : pdfUrl ? "Generated" : "Not generated"}
                    </span>

                    <div className="flex w-full flex-wrap items-center gap-2 sm:w-auto sm:gap-4">
                      <label className="flex items-center gap-2 text-xs font-semibold text-gray-700 select-none">
                        <span>Show Units</span>
                        <input
                          type="checkbox"
                          checked={!!showUnitBreakdown}
                          onChange={async (e) => {
                            const value = e.target.checked;
                            setShowUnitBreakdown(value);
                            if (!quoteId) return;
                            try {
                              await axiosInstance.patch(`/api/quotes/${quoteId}`, { show_unit_breakdown: value });
                              // Changing this impacts the PDF output; refresh immediately so preview stays in sync.
                              refreshPreview({ force: true });
                            } catch (err) {
                              console.error("Failed to update show_unit_breakdown", err);
                            }
                          }}
                          className="w-4 h-4"
                        />
                      </label>

                      <button
                        type="button"
                        onClick={() => refreshPreview({ force: false })}
                        disabled={!canPreview || previewLoading || saving || dirty}
                        className={`${btnGeneral} flex-1 rounded-full px-5 py-2 text-xs font-semibold shadow disabled:opacity-60 sm:flex-none`}
                        title={!quoteId ? "Create the quote first" : dirty ? "Save changes first" : previewLoading ? "Generating…" : "Refresh preview"}
                      >
                        {previewLoading ? "Generating…" : "Refresh Preview"}
                      </button>

                      {pdfUrl ? (
                        <a
                          href={pdfUrl}
                          target="_blank"
                          rel="noreferrer"
                          className={`${btnGeneral} flex-1 rounded-full px-4 py-2 text-center text-xs font-semibold shadow sm:flex-none lg:hidden`}
                          title="Open PDF in the browser viewer"
                        >
                          Open PDF
                        </a>
                      ) : null}
                    </div>
                  </div>

                  <div className="min-h-[68dvh] flex-1 bg-gray-50 lg:min-h-[360px]">
                    {pdfUrl ? (
                      <iframe
                        key={`pdf-${previewNonce}`}
                        src={pdfUrl}
                        title="Quote PDF preview"
                        className="h-[68dvh] w-full lg:h-full"
                        style={{ border: "none" }}
                      />
                    ) : (
                      <div className="h-full flex flex-col items-center justify-center p-6 text-xs text-gray-500 text-center">
                        {!quoteId ? <div>Create the quote to generate a PDF preview.</div> : <div>Refresh preview to view the PDF here.</div>}

                        {pdfTriedPath ? (
                          <div className="mt-2 text-[11px] text-gray-600">
                            Tried: <span className="font-mono">{pdfTriedPath}</span>
                            {pdfTriedStatus ? (
                              <span className="ml-2">
                                Status: <span className="font-mono">{pdfTriedStatus}</span>
                              </span>
                            ) : null}
                          </div>
                        ) : null}

                        {pdfErr ? <div className="mt-2 text-[11px] text-red-700 font-mono">{pdfErr}</div> : null}
                      </div>
                    )}
                  </div>
                </div>
</div>

              
{/* RIGHT */}
<div className={`${mobileWorkspace === "more" ? "flex" : "hidden"} min-h-0 min-w-0 flex-col gap-4 lg:flex`}>

  {/* Quote Images (slot 1/2) */}
  <div className="overflow-visible border-0 bg-white p-0 lg:overflow-hidden lg:rounded-lg lg:border lg:border-gray-200 lg:p-4">
    <div className="flex items-center justify-between">
      <label className={labelCls}>Quote Images (optional)</label>
      {quoteImagesSupported === "unsupported" ? (
        <span className="text-[11px] text-gray-500">API not enabled</span>
      ) : null}
    </div>

    <div className="mt-2 grid grid-cols-2 gap-1 rounded-xl bg-gray-100 p-1 lg:hidden">
      {[1, 2].map((slot) => (
        <button
          key={`image-tab-${slot}`}
          type="button"
          onClick={() => setMobileImageSlot(slot)}
          className={`rounded-lg px-3 py-2 text-[11px] font-extrabold ${
            mobileImageSlot === slot ? "bg-white text-gray-900 shadow-sm" : "text-gray-600"
          }`}
        >
          Image {slot}
        </button>
      ))}
    </div>

    <div className="mt-3 grid grid-cols-1 gap-3">
	      {[1, 2].map((slot) => {
	        const existing = quoteImages?.[slot];
	        const draft = imgDraft?.[slot] || {};
	        const serverUrlRaw = existing?.file_url || existing?.fileUrl || "";
	        const serverUrl = serverUrlRaw ? resolveStaticUrl(serverUrlRaw) : "";
	        const nonce = existing?._nonce ? String(existing._nonce) : "";
	        const serverUrlBusted = serverUrl && nonce ? `${serverUrl}${serverUrl.includes("?") ? "&" : "?"}v=${nonce}` : serverUrl;
	        const showUrl = draft?.previewUrl || serverUrlBusted || "";
        const captionVal = draft?.caption ?? existing?.caption ?? "";
        const disabledApi = quoteImagesSupported !== "ok";

        return (
          <div
            key={slot}
            className={`${mobileImageSlot === slot ? "block" : "hidden"} rounded-lg border border-gray-200 p-3 lg:block`}
          >
            <div className="flex flex-wrap items-center justify-between gap-2">
              <div className="text-[13px] font-semibold text-gray-800">Image {slot}</div>

              <div className="flex flex-wrap items-center gap-2">
                <button
                  type="button"
                  className={`${btnAction} px-3 py-1 rounded-md text-[12px] ${
                    quoteLocked || disabledApi ? "opacity-60 cursor-not-allowed" : ""
                  }`}
                  onClick={() => uploadQuoteImage(slot)}
                  disabled={quoteLocked || disabledApi}
                  title={disabledApi ? "Backend image endpoints not enabled yet" : "Upload image"}
                >
                  {draft?.file ? "Upload" : imageCaptionDirtySlots.includes(slot) ? "Save Caption" : "Upload"}
                </button>
                <button
                  type="button"
                  className={`${btnGeneral} px-3 py-1 rounded-md text-[12px] ${
                    quoteLocked || disabledApi ? "opacity-60 cursor-not-allowed" : ""
                  }`}
                  onClick={() => deleteQuoteImage(slot)}
                  disabled={quoteLocked || disabledApi}
                  title={disabledApi ? "Backend image endpoints not enabled yet" : "Remove image"}
                >
                  Remove
                </button>
              </div>
            </div>

            {showUrl ? (
              <div className="mt-2 border border-gray-200 rounded-md overflow-hidden bg-gray-50">
                {/* eslint-disable-next-line jsx-a11y/alt-text */}
                <img src={showUrl} className="w-full max-h-[180px] object-contain" />
              </div>
            ) : (
              <div className="mt-2 text-[12px] text-gray-500">No image selected.</div>
            )}

            <div className="mt-2">
              <input
                type="file"
                accept="image/*"
                className={`${fieldCls}`}
                onChange={(e) => pickQuoteImage(slot, e.target.files?.[0] || null)}
                disabled={quoteLocked || disabledApi}
              />
            </div>

            <div className="mt-2">
              <label className={`${labelCls} text-[12px]`}>Caption (optional)</label>
              <input
                type="text"
                value={captionVal}
                onChange={(e) => setQuoteImageCaption(slot, e.target.value)}
                className={`${fieldCls}`}
                placeholder="Optional caption"
                disabled={quoteLocked || disabledApi}
              />
            </div>

            <div className="mt-2 text-[11px] text-gray-600">
              Displays in the PDF below the tables (above terms). Stored on disk; DB stores URL.
            </div>
          </div>
        );
      })}
    </div>
  </div>

  {/* Specific Notes (HTML WYSIWYG; writes to quotes.specific_notes) */}
  {/* Insert independent HTML copies into the existing Specific Notes WYSIWYG. */}
  <div className="min-w-0 rounded-lg border border-gray-200 bg-white p-3 sm:p-4">
    <div className="flex flex-wrap items-center justify-between gap-2">
      <span className="text-xs font-semibold text-gray-800">Reusable Specific Notes</span>
      <button type="button" disabled={quoteLocked} className={`${btnGeneral} rounded-full px-3 py-2 text-xs disabled:opacity-50`}
        onClick={() => { if (!noteLibraryOpen) loadNoteLibrary(); setNoteLibraryOpen((v) => !v); }}>
        {noteLibraryOpen ? "Close reusable notes" : "Insert reusable notes"}
      </button>
    </div>
    {noteLibraryOpen && (
      <div className="mt-3 space-y-2">
        {noteLibraryBusy && <div className="text-xs">Loading notes…</div>}
        {noteLibraryError && <div role="alert" className="text-xs text-red-700">{noteLibraryError}</div>}
        {!noteLibraryBusy && noteLibrary.length === 0 && <div className="text-xs text-gray-600">No active reusable notes.</div>}
        {noteLibrary.map((note) => (
          <label key={note.id} className="flex cursor-pointer items-start gap-2 rounded border p-2 text-xs">
            <input type="checkbox" checked={noteLibrarySelection.includes(note.id)}
              onChange={(e) => setNoteLibrarySelection((old) => e.target.checked ? [...old, note.id] : old.filter((id) => id !== note.id))}
              disabled={quoteLocked} />
            <span className="break-words">{note.title}</span>
          </label>
        ))}
        <button type="button" disabled={quoteLocked || !noteLibrarySelection.length} className={`${btnGeneral} rounded-full px-4 py-2 text-xs disabled:opacity-50`}
          onClick={() => {
            // Each selected library entry becomes one editable numbered-list item.
            // Remove empty leading/trailing editor paragraphs instead of introducing
            // spacer paragraphs between snippets (the previous cause of five-line gaps).
            const blocks = noteLibrary
              .filter((item) => noteLibrarySelection.includes(item.id))
              .map((item) => String(item.body_html || "")
                .trim()
                .replace(/^(?:<p(?:\s[^>]*)?>\s*(?:<br\s*\/?>|&nbsp;|\s)*<\/p>\s*)+/gi, "")
                .replace(/(?:<p(?:\s[^>]*)?>\s*(?:<br\s*\/?>|&nbsp;|\s)*<\/p>\s*)+$/gi, "")
                .trim())
              .filter(Boolean);
            if (!blocks.length || quoteLocked) return;
            const listHtml = `<ol>${blocks.map((html) => `<li>${html}</li>`).join("")}</ol>`;
            setSpecificNotes((previous) => {
              const current = String(previous || "").trim();
              const hasContent = current.replace(/<p(?:\s[^>]*)?>\s*(?:<br\s*\/?>|&nbsp;|\s)*<\/p>/gi, "").trim();
              return hasContent ? `${current}${listHtml}` : listHtml;
            });
            markDirtyIfEditing();
            setNoteLibrarySelection([]);
            setNoteLibraryOpen(false);
          }}>Insert {noteLibrarySelection.length} selected</button>
      </div>
    )}
  </div>
  {/* Specific Notes (HTML WYSIWYG; writes to quotes.specific_notes) */}
  <div className="overflow-hidden rounded-lg border border-gray-200 bg-white p-3 sm:p-4">
    <label className={labelCls}>Specific Notes</label>
    <div className="mt-2 min-w-0">
      <TiptapEmailEditor
        value={specificNotes}
        onChange={(v) => {
          if (quoteLocked) return;
          setSpecificNotes(v);
          markDirtyIfEditing();
        }}
        placeholder="Optional notes. If provided, they appear next to the totals box on the last option only."
      />
    </div>
    <div className="mt-1 text-[11px] text-gray-600">
      Saves HTML to <span className="font-mono">quotes.specific_notes</span>.
    </div>
  </div>

  {/* Quick sanity */}
  <div className="hidden overflow-hidden rounded-lg border border-gray-200 bg-white p-4 lg:block">
    <div className="text-xs font-semibold text-gray-800">Quick sanity</div>
    <div className="mt-2 grid grid-cols-2 gap-2 text-[11px] text-gray-700">
      <div className="text-gray-500">Quote</div>
      <div className="font-mono">{quoteId ? `#${quoteId}` : "—"}</div>

      <div className="text-gray-500">Status</div>
      <div className="font-mono">{normStatus(quoteStatus) || "—"}</div>

      <div className="text-gray-500">Title</div>
      <div className="font-mono">{norm(quoteTitle) ? escapeHtml(quoteTitle) : "—"}</div>

      <div className="text-gray-500">Customer</div>
      <div className="font-mono">{toIntOrNull(customerId) ? `#${customerId}` : "—"}</div>

      <div className="text-gray-500">Contact</div>
      <div className="font-mono">{toIntOrNull(contactId) ? `#${contactId}` : "—"}</div>

      <div className="text-gray-500">Opportunity</div>
      <div className="font-mono">{toIntOrNull(effectiveOpportunityId) ? `#${effectiveOpportunityId}` : "—"}</div>

      <div className="text-gray-500">Quote terms doc</div>
      <div className="font-mono">{quoteTermsDocumentId ? `#${String(quoteTermsDocumentId)}` : "—"}</div>

      <div className="text-gray-500">Service terms doc</div>
      <div className="font-mono">{serviceTermsDocumentId ? `#${String(serviceTermsDocumentId)}` : "—"}</div>

      <div className="text-gray-500">Line items</div>
      <div className="font-mono">
        {lineItems.length} {quoteId ? (liEditable ? "(editable)" : "(locked)") : "(draft)"}
      </div>

      <div className="text-gray-500">Subtotal</div>
      <div className="font-mono">{money(totals.subtotal, currency)}</div>

      <div className="text-gray-500">VAT</div>
      <div className="font-mono">{money(totals.vatTotal, currency)}</div>

      <div className="text-gray-500">Total</div>
      <div className="font-mono">{money(totals.grandTotal, currency)}</div>

      <div className="text-gray-500">Dirty</div>
      <div className="font-mono">{String(!!dirty)}</div>

      <div className="text-gray-500">Preview nonce</div>
      <div className="font-mono">{String(previewNonce)}</div>
    </div>
  </div>

  {/* Next step (external component, mounted inside modal; no behaviour changes) */}
  <NextStepCard
    quoteId={quoteId}
    quoteLocked={quoteLocked}
    quoteStatus={quoteStatus}
    dirty={dirty || imageCaptionDirty}
    previewStale={previewStale}
    quoteTitle={quoteTitle}
    approverOptions={approverOptions}
    customerId={customerId}
    contactId={contactId}
    opportunityId={effectiveOpportunityId}
    lineItems={Array.isArray(lineItems) ? lineItems : []}
    contactEmail={toEmail}
    defaultTo={toEmail}
    lineItemsCount={Array.isArray(lineItems) ? lineItems.length : 0}
    pdfReady={!!pdfUrl}
    saving={saving}
    previewLoading={previewLoading}
    btnGeneral={btnGeneral}
    btnAction={btnAction}
    canCreateDraft={nsCanCreateDraft}
    canSaveChanges={nsCanSaveChanges}
    canSaveAndRefresh={nsCanSaveAndRefresh}
    canRefreshPreview={nsCanRefreshPreview}
    onCreateDraft={handleCreateDraft}
    onSaveChanges={handleSaveChanges}
    onSaveAndRefresh={handleSaveAndRefresh}
    onRefreshPreview={() => refreshPreview({ force: false })}
    onQuoteChanged={onQuoteChanged}
    onClose={onClose}
  />
</div>
{/* END RIGHT */}

            </div>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
}
